/**
 * Declarative catalog of **named** IAM allow-actions used across the client (nav guards, form checks, etc.).
 * It can list actions that are **not** (yet) returned by `GET features/getFeatureSettings` or that only apply
 * to a subset of apps.
 *
 * The **admin role matrix** (create/edit role) is driven by the feature-permission **API response** — not by
 * the size of this enum. The matrix is the intersection of the server catalog, plus any allow-actions already
 * stored on the role (shown as "Extra" when not in the catalog). Do not assume every enum value appears in the editor.
 */
export enum RESOURCE_PERMISSIONS {
    // Mailing List
    CREATE_MAILING_LIST = 'smartdining:mailingLists:createMailingList',
    UPDATE_MAILING_LIST = 'smartdining:mailingLists:updateMailingList',
    GET_MAILING_LIST_DETAILS = 'smartdining:mailingLists:getMailingListDetails',
    GET_MAILING_LIST = 'smartdining:mailingLists:getMailingList',
    GET_MAILING_LIST_BY_RESTAURANT = 'smartdining:mailingLists:getMailingListByRestaurant',
    DELETE_MAILING_LIST = 'smartdining:mailingLists:deleteMailingList',
    CREATE_MAILING_LIST_CONTACT = 'smartdining:mailingListContacts:createMailingListContact',
    CREATE_BULK_SUSCRIBERS = 'smartdining:legacy:create_bulk_suscribers',

    // Mailing List Contact
    UPDATE_MAILING_LIST_CONTACT = 'smartdining:mailingListContacts:updateMailingListContact',
    GET_MAILING_LIST_CONTACT_DETAILS = 'smartdining:mailingListContacts:getMailingListContactDetails',
    GET_MAILING_LIST_CONTACT = 'smartdining:mailingListContacts:getMailingListContact',
    GET_MAILING_LIST_CONTACT_LIST = 'smartdining:mailingListContacts:getMailingListContactList',
    DELETE_MAILING_LIST_CONTACT = 'smartdining:mailingListContacts:deleteMailingListContact',

    // Email and Sms Campaign Template
    CREATE_EMAIL_CAMPAIGN_TEMPLATE = "smartdining:emailCampaignTemplates:createEmailCampaignTemplate",
    GET_EMAIL_CAMPAIGN_TEMPLATE = "smartdining:emailCampaignTemplates:getEmailCampaignTemplate",
    GET_EMAIL_CAMPAIGN_TEMPLATES = "smartdining:emailCampaignTemplates:getEmailCampaignTemplates",
    GET_EMAIL_CAMPAIGN_TEMPLATES_BY_RESTAURANT = "smartdining:emailCampaignTemplates:getEmailCampaignTemplatesByRestaurant",
    UPDATE_EMAIL_CAMPAIGN_TEMPLATE = "smartdining:emailCampaignTemplates:updateEmailCampaignTemplate",
    DELETE_EMAIL_CAMPAIGN_TEMPLATE = "smartdining:emailCampaignTemplates:deleteEmailCampaignTemplate",
    UPDATE_SMS_CAMPAIGN = "smartdining:smsCampaigns:updateMarketingCampaign",

    //Marketing Subscription:
    CREATE_MARKETING_SUBSCRIPTION = "smartdining:marketingSubscriptions:createMarketingSubscription",
    UPDATE_MARKETING_SUBSCRIPTION = "smartdining:marketingSubscriptions:updateMarketingSubscription",
    DELETE_MARKETING_SUBSCRIPTION = "smartdining:marketingSubscriptions:deleteMarketingSubscription",
    LOAD_MARKETING_SUBSCRIPTION = "smartdining:marketingSubscriptions:loadMarketingSubscription",
    GET_MARKETING_SUBSCRIPTION_DETAILS = "smartdining:marketingSubscriptions:getMarketingSubscriptionDetails",
    GET_MARKETING_SUBSCRIPTION_LIST = "smartdining:marketingSubscriptions:getMarketingSubscriptionList",

    //Marketing Campaign:
    CREATE_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:createMarketingCampaign",
    UPDATE_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:updateMarketingCampaign",
    ENABLE_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:enableMarketingCampaign",
    DISABLE_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:disableMarketingCampaign",
    GET_MARKETING_CAMPAIGNS = "smartdining:marketingCampaigns:getMarketingCampaigns",
    GET_MARKETING_CAMPAIGN_DETAILS = "smartdining:marketingCampaigns:getMarketingCampaignDetails",
    DELETE_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:deleteMarketingCampaign",
    GET_MARKETING_CAMPAIGN_URL = "smartdining:marketingCampaigns:getUrl",
    REPLICATE_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:replicateMarketingCampaign",
    FINISH_LATER_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:finishMarketingCampaignLater",
    READY_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:makeReadyMarketingCampaign",
    SEND_EMAIL_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:sendEmailMarketingCampaign",
    SEND_SMS_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:sendSmsMarketingCampaign",
    SEND_EMAIL_MARKETING_CAMPAIGN_FOR_TESTERS = "smartdining:marketingCampaigns:sendEmailMarketingCampaignForTesters",
    UPDATE_EMAIL_TEMPLATES_FOR_MARKETING_CAMPAIGN = "smartdining:marketingCampaigns:updateEmailTemplatesForMarketingCampaign",
    SEND_SMS_MARKETING_CAMPAIGN_FOR_TESTERS = "smartdining:marketingCampaigns:sendSmsMarketingCampaignForTesters",
    GET_MARKETING_CAMPAIGN_ANALYTICS = "smartdining:marketingCampaigns:getMarketingCampaignAnalytics",

    /** WhatsApp / Messengers (Marketings extension) — IAM: marketingMessengers:* */
    ACCESS_MARKETING_MESSENGERS = "smartdining:marketingMessengers:access",
    MANAGE_MESSENGER_CAMPAIGNS = "smartdining:marketingMessengers:manageCampaigns",
    MANAGE_MESSENGER_INBOX = "smartdining:marketingMessengers:manageInbox",
    MANAGE_MESSENGER_CONTACTS = "smartdining:marketingMessengers:manageContacts",
    MANAGE_MESSENGER_TEMPLATES = "smartdining:marketingMessengers:manageTemplates",
    MANAGE_MESSENGER_AUTOMATIONS = "smartdining:marketingMessengers:manageAutomations",
    MANAGE_MESSENGER_COMMUNITIES = "smartdining:marketingMessengers:manageCommunities",
    VIEW_MESSENGER_ANALYTICS = "smartdining:marketingMessengers:viewAnalytics",
    MANAGE_MESSENGER_SETTINGS = "smartdining:marketingMessengers:manageSettings",

    /** Audience Management (extends segments; IAM: marketingAudiences:*) */
    GET_MARKETING_AUDIENCES = "smartdining:marketingAudiences:get",
    MANAGE_MARKETING_AUDIENCES = "smartdining:marketingAudiences:manage",
    MANAGE_MARKETING_AUDIENCE_TAGS = "smartdining:marketingAudiences:tags",
    IMPORT_MARKETING_AUDIENCES = "smartdining:marketingAudiences:import",
    VIEW_MARKETING_AUDIENCE_INSIGHTS = "smartdining:marketingAudiences:insights",

    /** Canonical subscribers (Nest marketing domain) — IAM: marketingSubscribers:* */
    GET_MARKETING_SUBSCRIBERS = "smartdining:marketingSubscribers:get",
    MANAGE_MARKETING_SUBSCRIBERS = "smartdining:marketingSubscribers:manage",

    /** Merge fields (Nest sd_subscriber_field_definitions) — IAM: marketingFieldDefinitions:* */
    GET_MARKETING_FIELD_DEFINITIONS = "smartdining:marketingFieldDefinitions:get",
    MANAGE_MARKETING_FIELD_DEFINITIONS = "smartdining:marketingFieldDefinitions:manage",

    /** Dynamic/static segments (Nest sd_marketing_segments) — IAM: marketingSegments:* */
    GET_MARKETING_SEGMENTS = "smartdining:marketingSegments:get",
    MANAGE_MARKETING_SEGMENTS = "smartdining:marketingSegments:manage",
    REBUILD_MARKETING_SEGMENTS = "smartdining:marketingSegments:rebuild",

    /** JSON/CSV subscriber import jobs (Nest `import/*`) — IAM: marketingImports:* */
    MANAGE_MARKETING_IMPORTS = "smartdining:marketingImports:manage",

    /** Platform + restaurant system audience ensure endpoints — IAM: marketingSystemAudiences:* */
    MANAGE_MARKETING_SYSTEM_AUDIENCES = "smartdining:marketingSystemAudiences:manage",

    // Customer
    DELETE_CUSTOMER = "smartdining:customers:deleteCustomer",
    ADD_CUSTOMER = "smartdining:customers:addCustomer",
    UPDATE_CUSTOMER = "smartdining:customers:updateCustomer",
    GET_CUSTOMER_DETAILS = "smartdining:customers:getCustomerDetails",
    VERIFY_CUSTOMER = "smartdining:customers:verifyCustomer",
    UPDATE_CUSTOMER_BY_ID = "smartdining:customers:updateCustomer",
    DELETE_RESTAURANT_CUSTOMER = "smartdining:customers:deleteRestaurantCustomer",
    SEARCH_CUSTOMERS = "smartdining:customers:search",
    GET_CUSTOMER_DATA = "smartdining:customers:getCustomerData",
    GET_RESTAURANT_CUSTOMERS = "smartdining:customers:getRestaurantCustomers",
    UPDATE_CUSTOMER_DATA = "smartdining:customers:updateCustomerData",
    ADD_CUSTOMER_NOTE = "smartdining:customers:addCustomerNote",
    UPDATE_CUSTOMER_NOTE = "smartdining:customers:updateCustomerNote",
    DELETE_CUSTOMER_NOTE = "smartdining:customers:deleteCustomerNote",
    ENABLE_MARKETING_PERMISSION = "smartdining:customers:enableMarketingPermission",
    DISABLE_MARKETING_PERMISSION = "smartdining:customers:disableMarketingPermission",
    ENABLE_MARKETING_SUBSCRIPTION_TO_ALL = "smartdining:customers:enableMarketingSubscriptionToAll",
    DISABLE_MARKETING_SUBSCRIPTION_TO_ALL = "smartdining:customers:disableMarketingSubscriptionToAll",
    SUBSCRIBE_MARKETING_SUBSCRIPTIONS = "smartdining:customers:subscribeMarketingSubscriptions",
    UNSUBSCRIBE_MARKETING_SUBSCRIPTIONS = "smartdining:customers:unsubscribeMarketingSubscriptions",

    // User
    DISABLE_USER = "smartdining:users:disableUser",
    UPDATE_USER = "smartdining:users:updateUser",
    ADD_ADDRESS = "smartdining:users:addAddress",
    GET_USER_POINT_REPORT = "smartdining:users:getUserPointReport",
    GET_ADDRESS = "smartdining:users:getAddress",
    DELETE_ADDRESS = "smartdining:users:deleteAddress",
    UPDATE_ADDRESS = "smartdining:users:updateAddress",
    UPLOAD_IMAGE = "smartdining:users:uploadImage",
    DELETE_IMAGE = "smartdining:users:deleteImage",
    SEARCH_USERS = "smartdining:users:searchUsers",
    GET_USER_BY_PHONE_OR_EMAIL = "smartdining:users:getUserByPhoneOrEmail",
    ENABLE_USER_MFA = "smartdining:users:enableUserMFA",
    DISABLE_USER_MFA = "smartdining:users:disableUserMFA",
    GET_USER = "smartdining:users:getUser",
    DISABLE_USER_API = "smartdining:users:disableUser",
    UPDATE_USER_INFO_BY_MANAGER = "smartdining:users:updateUserInfoByManager",
    UPDATE_USER_API = "smartdining:users:updateUser",
    CHANGE_USER_PASSWORD = "smartdining:users:changeUserPassword",
    DELETE_USER = "smartdining:users:deleteUser",
    UPDATE_DIETARY_RESTRICTIONS = "smartdining:users:updateDietaryRestrictions",
    ADD_DIETARY_RESTRICTION = "smartdining:users:addDietaryRestriction",
    ADD_DIETARY_RESTRICTIONS = "smartdining:users:addDietaryRestrictions",
    REMOVE_DIETARY_RESTRICTION = "smartdining:users:removeDietaryRestriction",
    GET_DIETARY_RESTRICTIONS = "smartdining:users:getDietaryRestrictions",

    // Platform staff / “account manager” operations (HTTP lives under {@code /api/v1/admins/...}; IAM uses {@code adminAccountManagers:*} and {@code admins:create} for create)
    UPDATE_MANAGER = "smartdining:adminAccountManagers:update",
    DISABLE_MANAGER = "smartdining:adminAccountManagers:disable",
    CREATE_MANAGER = "smartdining:admins:create",
    CANCEL_MANAGER = "smartdining:adminAccountManagers:cancel",
    ENABLE_MANAGER = "smartdining:adminAccountManagers:enable",
    GET_MANAGERS = "smartdining:adminAccountManagers:get",
    GET_MANAGER_ASSIGNED_COMPANIES = "smartdining:adminAccountManagers:get",
    GET_MANAGER_DETAILS = "smartdining:adminAccountManagers:get",
    CHECK_MANAGER_ACCOUNT = "smartdining:adminAccountManagers:check",
    ACTIVATE_ACCOUNT_MANAGER = "smartdining:adminAccountManagers:activate",

    // Orders
    GET_ORDER_PRICE = "smartdining:orders:getOrderPrice",
    GET_ORDER_CART_PRICE = "smartdining:orders:getOrderCartPrice",
    PLACE_ORDER = "smartdining:orders:placeOrder",
    CANCEL_ORDER = "smartdining:orders:cancelOrder",
    GET_ORDER_BY_ID = "smartdining:orders:getOrder",
    GET_ACTIVE_ORDERS_BY_RESTAURANT = "smartdining:orders:getOrdersActive",
    MAKE_ORDER_ACTIVE = "smartdining:orders:makeOrderActive",
    MAKE_ORDER_READY = "smartdining:orders:makeOrderReady",
    MAKE_ORDER_PREPARING = "smartdining:orders:makeOrderPreparing",
    NOTIFY_ORDER_READY = "smartdining:orders:notifyOrderReady",
    SEND_ORDER_RECEIPT = "smartdining:orders:sendOrderReceipt",
    MAKE_ORDER_COMPLETE = "smartdining:orders:makeOrderComplete",
    CHECK_IN_PICKUP = "smartdining:orders:checkInPickup",
    UNDO_ORDER_ACTION = "smartdining:orders:undoOrderAction",
    GET_PAID_ORDERS_BY_SHIFT_SETTINGS = "smartdining:orders:restaurant/getPaidOrdersByShiftSettings",
    GET_PAID_ORDER_BY_ORGANIZATION = "smartdining:orders:restaurant/getPaidOrderByOrganization",
    GET_PAID_ORDER_BY_RESTAURANT = "smartdining:orders:restaurant/getPaidOrderByRestaurant",
    GET_CUSTOMER_ORDERS = "smartdining:orders:getCustomerOrders",
    GET_ACTIVE_CUSTOMER_ORDERS = "smartdining:orders:getActiveCustomerOrders",
    GET_COMPLETED_CUSTOMER_ORDERS = "smartdining:orders:getCompletedCustomerOrders",
    SEARCH_ORDERS = "smartdining:orders:search",

    GET_PICKUP_SCHEDULE = "smartdining:orderPickup:getPickupSchedule",
    GET_RESTAURANTS_PICKUP_SCHEDULES = "smartdining:orderPickup:getRestaurantsPickupSchedules",
    GET_PICKUP_PREP_TIME = "smartdining:orderPickup:getPickupPrepTime",
    GET_RESTAURANTS_PICKUP_QUOTE_TIMES = "smartdining:orderPickup:getRestaurantsPickupQuoteTimes",
    GET_RESTAURANT_ORDER_SCHEDULES = "smartdining:orderPickup:getRestaurantOrderSchedules",
    UPDATE_PICKUP_SETTINGS = "smartdining:orderPickup:updatePickupSettings",
    GET_PICKUP_SETTINGS = "smartdining:orderPickup:getPickupSettings",
    GET_SHIFT_PICKUP_SETTINGS = "smartdining:orderPickup:getShiftPickupSettings",

    // Restaurants
    CREATE_RESTAURANT = "smartdining:restaurants:createRestaurant",
    UPDATE_RESTAURANT = "smartdining:restaurants:updateRestaurant",
    UPLOAD_RESTAURANT_LOGO = "smartdining:restaurants:uploadLogo",
    ADD_RESTAURANT_PHOTO_GALLERY = "smartdining:restaurants:addPhotoGallery",
    REMOVE_RESTAURANT_GALLERY = "smartdining:restaurants:removeGallery",
    GET_RESTAURANT_PHOTO_GALLERY = "smartdining:restaurants:getPhotoGallery",
    UPLOAD_RESTAURANT_BANNER = "smartdining:restaurants:uploadBanner",
    ENABLE_RESTAURANT = "smartdining:restaurants:enableRestaurant",
    DISABLE_RESTAURANT = "smartdining:restaurants:disableRestaurant",
    DELETE_RESTAURANT = "smartdining:restaurants:deleteRestaurant",
    CREATE_CUSTOM_MESSAGE = "smartdining:restaurants:createCustomMessage",
    UPDATE_CUSTOM_MESSAGE = "smartdining:restaurants:updateCustomMessage",
    GET_RESTAURANT_CUSTOM_MESSAGE = "smartdining:restaurants:getCustomMessage",
    ENABLEMENT_LOYALTY_SETTINGS = "smartdining:restaurants:setLoyaltySettings",
    ENABLEMENT_SURVEY_SETTINGS = "smartdining:restaurants:setSurveySettings",
    ENABLEMENT_ORDER_SETTINGS = "smartdining:restaurants:setOrderSettings",
    ENABLEMENT_RESERVATION_SETTINGS = "smartdining:restaurants:setReservationSettings",
    ENABLEMENT_WAITLIST_SETTINGS = "smartdining:restaurants:setWaitListSettings",
    ENABLEMENT_PAYMENT_SETTINGS = "smartdining:restaurants:setPaymentSettings",
    ENABLEMENT_RESERVATION_POLICIES = "smartdining:restaurants:setReservationPolicies",
    CREATE_SPECIAL_DAY = "smartdining:restaurants:createSpecialDay",
    GET_RESTAURANT_SPECIAL_DAYS = "smartdining:restaurants:getSpecialDays",
    IS_RESTAURANT_TODAY_SPECIAL_DAYS = "smartdining:restaurants:isTodaySpecialDays",
    UPDATE_RESTAURANT_SPECIAL_DAY = "smartdining:restaurants:updateSpecialDay",
    DELETE_RESTAURANT_SPECIAL_DAY = "smartdining:restaurants:deleteSpecialDay",
    GET_RESTAURANT_PATH_DATA = "smartdining:restaurants:getRestaurantPathData",
    GET_RESTAURANT_DETAILS = "smartdining:restaurants:getRestaurantDetails",
    GET_RESTAURANT_DETAILS_BY_EMPLOYEE = "smartdining:restaurants:getRestaurantDetailsByEmployee",
    ADMIN_GET_ALL_RESTAURANTS = "smartdining:restaurants:admin/getAllRestaurants",
    ADMIN_FILTER_RESTAURANTS = "smartdining:restaurants:admin/filterRestaurants",
    GET_ACCOUNT_RESTAURANTS = "smartdining:restaurants:getAccountRestaurants",
    GET_OWNER_RESTAURANTS = "smartdining:restaurants:getOwnerRestaurants",
    GET_RESTAURANT_COUNT = "smartdining:restaurants:getRestaurantCount",
    SUGGEST_RESTAURANTS = "smartdining:restaurants:suggest",
    SEARCH_RESTAURANTS = "smartdining:restaurants:search",
    SEARCH_RESTAURANTS_BY_ADMIN = "smartdining:restaurants:searchByAdmin",
    ADMIN_CREATE_RESTAURANT = "smartdining:restaurants:admin/createRestaurant",
    ADMIN_SET_DISCOVERABLE_ONLINE = "smartdining:restaurants:admin/setDiscoverableOnline",
    ADMIN_RESTAURANT_CLAIMS = "smartdining:restaurantClaims:admin",
    ADMIN_REVIEW_RESTAURANT_CLAIM = "smartdining:restaurantClaims:admin/review",
    /** Legacy catalog key; prefer {@link IAM_MENU_ACTIONS.apiKeysGet} for ngx-permissions checks. */
    ADMIN_ORGANIZATION_API_KEYS = "smartdining:organizations:admin/api-keys",

    // Cuisines
    CREATE_CUISINE = "smartdining:cuisines:createCuisine",
    UPDATE_CUISINE_IMAGE = "smartdining:cuisines:updateImage",
    GET_CUISINES = "smartdining:cuisines:getCuisines",
    SEARCH_CUISINES = "smartdining:cuisines:searchCuisines",
    GET_DELETED_CUISINES = "smartdining:cuisines:getDeletedCuisines",
    GET_CUISINE_DETAILS = "smartdining:cuisines:getCuisineDetails",
    ENABLE_CUISINE = "smartdining:cuisines:enableCuisine",
    DISABLE_CUISINE = "smartdining:cuisines:disableCuisine",
    UPDATE_CUISINE = "smartdining:cuisines:updateCuisine",
    PATCH_CUISINE = "smartdining:cuisines:patchCuisine",
    DELETE_CUISINE = "smartdining:cuisines:deleteCuisine",

    // Employee
    ADD_EMPLOYEE = "smartdining:employees:addEmployee",
    GET_EMPLOYEES_BY_ROLE = "smartdining:employees:getEmployeesByRole",
    UPDATE_EMPLOYEE = "smartdining:employees:updateEmployee",
    GET_EMPLOYEES_BY_RESTAURANT = "smartdining:employees:getEmployees",
    ADMIN_SEARCH_EMPLOYEES = "smartdining:employees:adminSearch",
    GET_EMPLOYEE_DETAILS = "smartdining:employees:getEmployeeDetails",
    DELETE_EMPLOYEE = "smartdining:employees:deleteEmployee",
    ENABLE_EMPLOYEE = "smartdining:employees:enableEmployee",
    DISABLE_EMPLOYEE = "smartdining:employees:disableEmployee",
    CHECK_EMPLOYEE_ACCOUNT = "smartdining:employees:checkEmployeeAccount",
    ACTIVATE_ACCOUNT_EMPLOYEE = "smartdining:employees:activateAccountEmployee",
    SEARCH_EMPLOYEES = "smartdining:employees:search",
    /** Create/manage internal (Smart Dining platform) employee records. */
    EMPLOYEES_MANAGE_INTERNAL_SCOPE = "smartdining:employees:manageInternalScope",
    /** Create/manage restaurant-scoped employee records. */
    EMPLOYEES_MANAGE_RESTAURANT_SCOPE = "smartdining:employees:manageRestaurantScope",
    /** Cross-tenant employee directory (super admin / operations). Server must enforce. */
    EMPLOYEES_VIEW_ALL_ORGANIZATIONS = "smartdining:employees:viewAllOrganizations",

    /** HR admin hub: lifecycle, onboarding, documents, agreements (IAM `hr:*`) */
    HR_ADMIN_READ = "smartdining:hr:admin:read",
    HR_ONBOARDING_READ = "smartdining:hr:onboarding:read",
    HR_ONBOARDING_WRITE = "smartdining:hr:onboarding:write",
    HR_LIFECYCLE_MANAGE = "smartdining:hr:lifecycle:manage",
    HR_DOCUMENTS_READ = "smartdining:hr:documents:read",
    HR_DOCUMENTS_WRITE = "smartdining:hr:documents:write",
    HR_AGREEMENTS_READ = "smartdining:hr:agreements:read",
    HR_AGREEMENTS_WRITE = "smartdining:hr:agreements:write",
    HR_AUDIT_READ = "smartdining:hr:audit:read",
    HR_SETTINGS_READ = "smartdining:hr:settings:read",
    HR_SETTINGS_WRITE = "smartdining:hr:settings:write",
    TIME_CLOCKING_ADMIN_EMPLOYEE_EVENTS = "smartdining:timeClocking:admin:employeeEvents",

    /** Smart Dining internal company expenses & accounting (not tenant/restaurant billing). */
    INTERNAL_ACCOUNTING_VIEW = "smartdining:internalAccounting:view",
    INTERNAL_ACCOUNTING_MANAGE = "smartdining:internalAccounting:manage",
    INTERNAL_ACCOUNTING_APPROVE = "smartdining:internalAccounting:approve",

    // Time clocking (IAM-aligned `timeClocking:*` actions)
    TIME_CLOCKING_SELF_LOG = "smartdining:timeClocking:self:log",
    TIME_CLOCKING_SELF_GET_EVENTS = "smartdining:timeClocking:self:getEvents",
    TIME_CLOCKING_SELF_GET_SUMMARY = "smartdining:timeClocking:self:getSummary",
    TIME_CLOCKING_VACATION_REQUEST = "smartdining:timeClocking:vacation:request",
    TIME_CLOCKING_VACATION_LIST_MINE = "smartdining:timeClocking:vacation:listMine",
    TIME_CLOCKING_VACATION_LIST_PENDING = "smartdining:timeClocking:vacation:listPending",
    TIME_CLOCKING_VACATION_APPROVE = "smartdining:timeClocking:vacation:approve",
    TIME_CLOCKING_VACATION_DENY = "smartdining:timeClocking:vacation:deny",
    TIME_CLOCKING_ADMIN_REPORTS = "smartdining:timeClocking:admin:reports",
    TIME_CLOCKING_SETTINGS_GET = "smartdining:timeClocking:settings:get",
    TIME_CLOCKING_SETTINGS_UPDATE = "smartdining:timeClocking:settings:update",

    // Employee Schedule #
    ADD_EMPLOYEE_SCHEDULE = "smartdining:employeeSchedules:addEmployeeSchedule",
    UPDATE_EMPLOYEE_SCHEDULE = "smartdining:employeeSchedules:updateEmployeeSchedule",
    GET_RESTAURANT_EMPLOYEE_SCHEDULES = "smartdining:employeeSchedules:getRestaurantEmployeeSchedules",
    ADMIN_SEARCH_EMPLOYEE_SCHEDULES = "smartdining:employeeSchedules:adminSearch",
    GET_EMPLOYEE_SCHEDULE_DETAILS = "smartdining:employeeSchedules:getEmployeeScheduleDetails",
    GET_EMPLOYEE_SCHEDULES_BY_ID = "smartdining:employeeSchedules:getEmployeeSchedules",
    DELETE_EMPLOYEE_SCHEDULE = "smartdining:employeeSchedules:deleteEmployeeSchedule",
    ENABLE_EMPLOYEE_SCHEDULE = "smartdining:employeeSchedules:enableEmployeeSchedule",
    DISABLE_EMPLOYEE_SCHEDULE = "smartdining:employeeSchedules:disableEmployeeSchedule",
    SEARCH_EMPLOYEE_SCHEDULES = "smartdining:employeeSchedules:search",

    // Employee Task #
    ADD_EMPLOYEE_TASK = "smartdining:employeeTasks:addEmployeeTask",
    UPDATE_EMPLOYEE_TASK = "smartdining:employeeTasks:updateEmployeeTask",
    GET_RESTAURANT_EMPLOYEE_TASKS = "smartdining:employeeTasks:getRestaurantEmployeeTasks",
    ADMIN_SEARCH_EMPLOYEE_TASKS = "smartdining:employeeTasks:adminSearch",
    GET_EMPLOYEE_TASKS_BY_ID = "smartdining:employeeTasks:getEmployeeTasks",
    GET_EMPLOYEE_TASK_DETAILS = "smartdining:employeeTasks:getEmployeeDetailsTask",
    DELETE_EMPLOYEE_TASK = "smartdining:employeeTasks:deleteEmployeeTask",
    ENABLE_EMPLOYEE_TASK = "smartdining:employeeTasks:enableEmployeeTask",
    DISABLE_EMPLOYEE_TASK = "smartdining:employeeTasks:disableEmployeeTask",
    ACTIVE_EMPLOYEE_TASK = "smartdining:employeeTasks:activeEmployeeTask",
    SEARCH_EMPLOYEE_TASKS = "smartdining:employeeTasks:search",

    // Server Section #
    CREATE_SERVER_SECTION = "smartdining:serverSections:createSeverSection",
    GET_RESTAURANT_SERVERS = "smartdining:serverSections:getRestaurantServers",
    UPDATE_SERVER_SECTION = "smartdining:serverSections:updateSeverSection",
    GET_UNASSIGNED_SERVERS = "smartdining:serverSections:getUnassignedServers",
    GET_ASSIGNED_SERVERS = "smartdining:serverSections:getAssignedServers",
    GET_SERVER_SECTIONS = "smartdining:serverSections:getSeverSections",
    DELETE_SERVER_SECTION = "smartdining:serverSections:deleteServerSection",

    // Server Section Setting #
    CREATE_SERVER_SECTION_SETTING = "smartdining:serverSectionSettings:createServerSectionSetting",
    UPDATE_SERVER_SECTION_SETTING = "smartdining:serverSectionSettings:updateServerSectionSetting",
    GET_SERVER_SECTION_SETTING_DETAILS = "smartdining:serverSectionSettings:getServerSectionSettingDetails",
    GET_SERVER_SECTION_SETTINGS_BY_RESTAURANT = "smartdining:serverSectionSettings:getServerSectionSettings",
    DELETE_SERVER_SECTION_SETTING = "smartdining:serverSectionSettings:deleteServerSectionSetting",

    // Menus #
    CREATE_MENU = "smartdining:menus:createMenu",
    GET_MENU = "smartdining:menus:getMenu",
    UPDATE_MENU = "smartdining:menus:updateMenu",
    DELETE_MENU = "smartdining:menus:deleteMenu",
    DISABLE_MENU = "smartdining:menus:disabledMenu",
    ENABLE_MENU = "smartdining:menus:enableMenu",
    RESTORE_DELETED_MENU = "smartdining:menus:restoreDeleteMenu",
    UPDATE_MENU_POSITION = "smartdining:menus:updateMenuPosition",
    GET_RESTAURANT_MENU_LIST = "smartdining:menus:getRestaurantMenuList",
    GET_DINING_MENUS = "smartdining:menus:getDiningMenus",
    GET_PICKUP_MENUS = "smartdining:menus:getPickupMenus",
    GET_CATERING_MENUS = "smartdining:menus:getCateringMenus",
    GET_ONLINE_MENU = "smartdining:menus:getOnlineMenu",
    GET_POS_MENUS = "smartdining:menus:shifts/getPosMenus",
    GET_RESTAURANT_MENUS = "smartdining:menus:restaurant/getMenus",
    GET_DELETED_RESTAURANT_MENUS = "smartdining:menus:restaurant/getDeletedMenus",
    GET_MENUS_ONLY = "smartdining:menus:restaurant/getMenusOnly",
    SEARCH_MENUS = "smartdining:menus:search",

    // Menu Categories #
    CREATE_MENU_CATEGORY = "smartdining:menusCategories:createCategory",
    GET_CATEGORY = "smartdining:menusCategories:getCategory",
    GET_CATEGORIES = "smartdining:menusCategories:getCategories",
    GET_DELETED_CATEGORIES = "smartdining:menusCategories:getDeletedCategories",
    UPDATE_CATEGORY = "smartdining:menusCategories:updateCategory",
    DISABLE_CATEGORY = "smartdining:menusCategories:disabledCategory",
    ENABLE_CATEGORY = "smartdining:menusCategories:enableCategory",
    RESTORE_DELETED_CATEGORY = "smartdining:menusCategories:restoreDeleteCategory",
    UPDATE_CATEGORY_POSITIONS = "smartdining:menusCategories:updateCategoryPositions",
    DELETE_CATEGORY = "smartdining:menusCategories:deleteCategory",

    // Menu Items #
    CREATE_MENU_ITEM = "smartdining:menuItems:createMenuItem",
    GET_MENU_ITEM_DETAILS = "smartdining:menuItems:getMenuItemDetails",
    UPDATE_MENU_ITEM = "smartdining:menuItems:updateMenuItem",
    UPDATE_MANY_MENU_ITEMS = "smartdining:menuItems:updateManyMenuItems",
    GET_MENU_ITEMS = "smartdining:menuItems:getMenuItems",
    SEARCH_MENU_ITEMS = "smartdining:menuItems:search",
    GET_DELETED_MENU_ITEMS = "smartdining:menuItems:getDeletedMenuItems",
    LOAD_MENU_ITEM_LOGO = "smartdining:menuItems:loadMenuItemLogo",
    DELETE_MENU_ITEM = "smartdining:menuItems:deleteMenuItem",

    // Menu Modifier Groups #
    GET_MODIFIER_GROUPS = "smartdining:menuModifierGroups:getModifierGroups",
    GET_DELETED_MODIFIER_GROUPS = "smartdining:menuModifierGroups:getDeletedModifierGroups",
    GET_MODIFIER_GROUP_DETAILS = "smartdining:menuModifierGroups:getModifierGroupDetails",
    CREATE_MODIFIER_GROUP = "smartdining:menuModifierGroups:createModifierGroup",
    UPDATE_MODIFIER_GROUP = "smartdining:menuModifierGroups:updateModifierGroup",
    DELETE_MODIFIER_GROUP = "smartdining:menuModifierGroups:deleteModifierGroup",
    DISABLE_MODIFIER_GROUP = "smartdining:menuModifierGroups:disabledModifierGroup",
    ENABLE_MODIFIER_GROUP = "smartdining:menuModifierGroups:enableModifierGroup",
    RESTORE_DELETED_MODIFIER_GROUP = "smartdining:menuModifierGroups:restoreDeleteModifierGroup",

    // Reservations #
    RESERVATIONS_RESTAURANT_GET_UPCOMING_RESERVATIONS = "smartdining:reservations:restaurant/geUpcomingReservations",
    RESERVATIONS_USER_GET_UPCOMING_RESERVATIONS_V2 = "smartdining:reservations:user/getUpcomingReservationsV2",
    RESERVATIONS_USER_PAST_RESERVATIONS_V2 = "smartdining:reservations:user/pastReservationsV2",
    RESERVATIONS_ADMIN_COUNT_RESERVATIONS = "smartdining:reservations:admin/countReservations",
    RESERVATIONS_ADMIN_GET_TOTAL_RESERVATION_PER_WEEK = "smartdining:reservations:admin/getTotalReservationPerWeek",
    RESERVATIONS_MAKE_RESERVATION = "smartdining:reservations:makeReservation",
    RESERVATIONS_GET_RESERVATION_DETAILS = "smartdining:reservations:getReservationDetails",
    RESERVATIONS_CANCEL_RESERVATION = "smartdining:reservations:cancelReservation",
    RESERVATIONS_UPDATE_RESERVATION = "smartdining:reservations:updateReservation",
    RESERVATIONS_CHECKIN_RESERVATION = "smartdining:reservations:checkinReservation",
    RESERVATIONS_TABLE_READY = "smartdining:reservations:tableReady",
    RESERVATIONS_RESTAURANT_GET_UPCOMING_RESERVATIONS_V2 = "smartdining:reservations:restaurant/geUpcomingReservations",
    RESERVATIONS_USER_GET_RESERVATIONS = "smartdining:reservations:user/getReservations",

    // Waitlist
    MAKE_WAIT_LIST_READY = "smartdining:waitlist:makeWaitListReady",
    WAITLIST_ADD_GUEST = "smartdining:waitlist:addGuestToWaitList",
    WAITLIST_GET_DETAILS = "smartdining:waitlist:getWaitListDetails",
    WAITLIST_GET_ALL = "smartdining:waitlist:getWaitList",
    WAITLIST_GET_WAIT_TIMES_ON_TABLE = "smartdining:waitlist:getWaitTimesOnTable",
    WAITLIST_CHECKIN = "smartdining:waitlist:checkinWaitList",
    WAITLIST_UPDATE = "smartdining:waitlist:updateWaitList",
    WAITLIST_CANCEL = "smartdining:waitlist:cancelWaitList",
    WAITLIST_TABLE_READY = "smartdining:waitlist:tableReady",

    // Booking #
    MAKE_RESERVATION_READY = "smartdining:bookings:makeReservationReady",
    GET_NO_SHOW_BOOKINGS = "smartdining:bookings:restaurant/getNoShowBookings",
    GET_PAID_CHECKS = "smartdining:bookings:getPaidChecks",
    GET_TIME_AVAILABILITIES = "smartdining:bookings:getTimeAvailabilities",
    GET_TIME_AVAILABILITIES_FOR_RESTAURANTS = "smartdining:bookings:getTimeAvailabilitiesForRestaurants",
    GET_WAIT_TIMES = "smartdining:bookings:getWaitTimes",
    GET_WAIT_TIME_FOR_TABLES = "smartdining:bookings:getWaitTimeForTables",
    GET_BOOKING_DETAILS = "smartdining:bookings:getBookingDetails",
    CLOSE_BOOKING = "smartdining:bookings:closeBooking",
    OPEN_BOOKING = "smartdining:bookings:openBooking",
    TIMELINE_BOOKINGS = "smartdining:bookings:restaurant/timelineBookings",
    GET_FINISHED_BOOKINGS = "smartdining:bookings:restaurant/getFinishedBookings",
    GET_CANCELLED_BOOKINGS = "smartdining:bookings:restaurant/getCancelledBookings",
    GET_NO_SHOW_BOOKINGS_RESTAURANT = "smartdining:bookings:restaurant/getNoShowBookings",

    // Floors #
    CREATE_BULK_ROOMS = "smartdining:rooms:restaurant/createBulkRooms",
    UPDATE_BULK_ROOMS = "smartdining:rooms:restaurant/updateBulkRooms",
    CREATE_ROOM = "smartdining:rooms:restaurant/createRoom",
    UPDATE_ROOM = "smartdining:rooms:restaurant/updateRoom",
    GET_ROOM_DETAILS = "smartdining:rooms:restaurant/getRoomDetails",

    // Room Settings
    UPDATE_BULK_ROOMS_SETTINGS = "smartdining:roomSettings:updateBulkRoomsSettings",
    GET_ROOMS_SETTINGS_DETAILS = "smartdining:roomSettings:getRoomDetailsSettings",
    GET_BULK_ROOMS_SETTINGS = "smartdining:roomSettings:getBulkRoomsSettings",
    CREATE_ROOM_SETTINGS = "smartdining:roomSettings:createRoomSettings",
    UPDATE_ROOM_SETTINGS = "smartdining:roomSettings:updateRoomSettings",
    DELETE_ROOM_SETTINGS = "smartdining:roomSettings:deleteRoomSettings",


    // Shifts #
    ADD_SHIFT_NOTE = "smartdining:shift:addNote",
    UPDATE_SHIFT = "smartdining:shift:updateShift",
    CREATE_SHIFT = "smartdining:shift:create",
    GET_SHIFTS = "smartdining:shift:getShifts",
    UPDATE_SHIFT_BY_ID = "smartdining:shift:updateShift",
    GET_RESTAURANT_SHIFTS = "smartdining:shift:getRestaurantShifts",
    ADD_SHIFT_NOTE_BY_ID = "smartdining:shift:addNote",
    UPDATE_SHIFT_NOTE = "smartdining:shift:updateNote",
    DELETE_SHIFT_NOTE = "smartdining:shift:deleteNote",
    GET_SHIFT_NOTES = "smartdining:shift:getNotes",

    // Table #
    DIRTY_TABLE = "smartdining:tables:dirtyTable",
    CREATE_BULK_TABLES = "smartdining:tables:createBulkTables",
    UPDATE_TABLE = "smartdining:tables:updateTable",
    MOVE_BACK_TABLE = "smartdining:tables:moveBackTable",
    OPEN_TABLE = "smartdining:tables:openTable",
    CREATE_TABLE = "smartdining:tables:createTable",
    ASSIGN_TABLE = "smartdining:tables:servers/assignTable",
    GET_TABLE_DETAILS = "smartdining:tables:getTableDetails",
    GET_ROOMS_TABLES = "smartdining:tables:getRoomsTables",
    GET_DAY_TABLES = "smartdining:tables:getDayTables",
    GET_AVAILABLE_TABLES = "smartdining:tables:getAvailableTables",
    TRANSFER_TABLE = "smartdining:tables:transferTable",
    CLOSE_TABLE = "smartdining:tables:closeTable",
    REMOVE_TABLE = "smartdining:tables:removeTable",
    BLOCK_TABLE = "smartdining:tables:blockTable",
    UNBLOCK_TABLE = "smartdining:tables:unblockTable",
    UNDIRTY_TABLE = "smartdining:tables:unDirtyTable",
    UNASSIGNED_TABLE = "smartdining:tables:servers/unassignedTable",
    DELETE_TABLE = "smartdining:tables:deleteTable",

    // Table Settings
    CREATE_BULK_TABLE_SETTINGS = "smartdining:tableSettings:createBulkTableSettings",
    CREATE_TABLE_SETTINGS = "smartdining:tableSettings:createTableSettings",
    UPDATE_BULK_TABLE_SETTINGS = "smartdining:tableSettings:updateBulkTableSettings",
    UPDATE_TABLE_SETTINGS = "smartdining:tableSettings:updateTableSettings",
    DELETE_TABLE_SETTINGS = "smartdining:tableSettings:deleteTableSettings",
    CREATE_TABLE_SETTINGS_NEW = "smartdining:tableSettings:createTableSettings",
    CREATE_BULK_TABLE_SETTINGS_NEW = "smartdining:tableSettings:createBulkTableSettings",
    GET_BULK_TABLE_SETTINGS_BY_ROOM = "smartdining:tableSettings:getBulkTableSettingsByRoom",
    GET_TABLE_SETTINGS_DETAILS = "smartdining:tableSettings:getTableSettingsDetails",
    GET_BULK_TABLE_SETTINGS_BY_RESTAURANT = "smartdining:tableSettings:getBulkTableSettings",
    UPDATE_BULK_TABLE_SETTINGS_NEW = "smartdining:tableSettings:updateBulkTableSettings",
    DELETE_TABLE_SETTINGS_NEW = "smartdining:tableSettings:deleteTableSettings",


    // Shift  Settings#
    GET_SHIFT_SETTINGS = "smartdining:shiftSettings:getShiftSettings",
    GET_WEEKDAY_SHIFT_SETTINGS = "smartdining:shiftSettings:getWeekdayShiftSettings",
    GET_SHIFT_SETTINGS_DETAILS = "smartdining:shiftSettings:getShiftSettingsDetails",
    UPDATE_SHIFT_SETTINGS_BY_ID = "smartdining:shiftSettings:updateShiftSettings",
    DELETE_SHIFT_SETTINGS_BY_ID = "smartdining:shiftSettings:deleteShiftSettings",
    CREATE_BULK_SHIFT_SETTINGS = "smartdining:shiftSettings:createBulkShiftSettings",
    CREATE_SHIFT_SETTINGS = "smartdining:shiftSettings:createShiftSettings",
    UPDATE_SHIFT_SETTINGS = "smartdining:shiftSettings:updateShiftSettings",
    DELETE_SHIFT_SETTINGS = "smartdining:shiftSettings:deleteShiftSettings",

    // Floors #
    GET_ROOM_DETAILS_BY_ID = "smartdining:rooms:restaurant/getRoomDetails",
    GET_ROOMS = "smartdining:rooms:restaurant/getRooms",
    GET_SHIFT_ROOMS = "smartdining:rooms:shifts/getShiftRooms",
    CREATE_ROOM_BY_RESTAURANT = "smartdining:rooms:restaurant/createRoom",
    CREATE_BULK_ROOMS_BY_RESTAURANT = "smartdining:rooms:restaurant/createBulkRooms",
    UPDATE_ROOM_BY_ID = "smartdining:rooms:restaurant/updateRoom",
    UPDATE_BULK_ROOMS_BY_RESTAURANT = "smartdining:rooms:restaurant/updateBulkRooms",

    // Room Settings#
    GET_ROOM_DETAILS_SETTINGS = "smartdining:roomSettings:getRoomDetailsSettings",
    GET_BULK_ROOMS_SETTINGS_BY_RESTAURANT = "smartdining:roomSettings:getBulkRoomsSettings",
    UPDATE_ROOM_SETTINGS_BY_ID = "smartdining:roomSettings:updateRoomSettings",
    DELETE_ROOM_SETTINGS_BY_ID = "smartdining:roomSettings:deleteRoomSettings",

    // Subscription Coupon (IAM {@code subscriptionCoupons:*})
    CREATE_SUBSCRIPTION_COUPON = "subscriptionCoupons:create",
    UPDATE_SUBSCRIPTION_COUPON = "subscriptionCoupons:update",
    DELETE_SUBSCRIPTION_COUPON = "subscriptionCoupons:delete",
    ENABLE_SUBSCRIPTION_COUPON = "subscriptionCoupons:enable",
    DISABLE_SUBSCRIPTION_COUPON = "subscriptionCoupons:disable",
    GET_SUBSCRIPTION_COUPONS = "subscriptionCoupons:get",
    GET_SUBSCRIPTION_COUPON_DETAILS = "subscriptionCoupons:get",
    SEARCH_SUBSCRIPTION_COUPONS = "subscriptionCoupons:search",
    GET_COUPON_DETAILS_BY_PROMO_CODE = "subscriptionCoupons:get",

    // Subscription plan / product packages (IAM {@code subscriptionPlans:*}) — API: {@code /api/v1/products}
    CREATE_SUBSCRIPTION_PLAN = "subscriptionPlans:create",
    GET_SUBSCRIPTION_PLANS = "subscriptionPlans:get",
    GET_DELETE_SUBSCRIPTION_PLANS = "subscriptionPlans:get",
    GET_SUBSCRIPTION_PLAN = "subscriptionPlans:get",
    UPDATE_SUBSCRIPTION_PLAN = "subscriptionPlans:update",
    DELETE_SUBSCRIPTION_PLAN = "subscriptionPlans:delete",

    // Subscriptions (IAM {@code subscriptions:*})
    MAKE_SUBSCRIPTION = "subscriptions:create",
    UPDATE_SUBSCRIPTION = "subscriptions:update",
    UPDATE_USER_SPECIAL_SUBSCRIPTION = "subscriptions:update",
    CREATE_SPECIAL_SUBSCRIPTION = "subscriptions:create",
    APPLY_DISCOUNT_TO_SUBSCRIPTION = "subscriptions:apply",
    GET_COMPANY_SUBSCRIPTION = "subscriptions:get",
    GET_SUBSCRIPTION_DETAILS = "subscriptions:get",
    SEARCH_SUBSCRIPTIONS = "subscriptions:get",
    CANCEL_SUBSCRIPTION = "subscriptions:cancel",

    // Entitlements / feature catalog (mixed gates: writes often {@code subscriptions:*}, reads {@code subscriptionPlans:get}, search {@code features:search}, delete {@code features:delete})
    CREATE_FEATURE = "subscriptions:create",
    UPDATE_FEATURE = "subscriptions:update",
    GET_FEATURE = "subscriptionPlans:get",
    GET_DELETED_FEATURE = "subscriptionPlans:get",
    DELETE_FEATURE = "features:delete",
    GET_SUBSCRIPTION_FEATURES = "subscriptionPlans:get",
    GET_FEATURE_SETTINGS = "subscriptionPlans:get",
    GET_APP_FEATURES = "subscriptionPlans:get",
    GET_USED_FEATURES = "subscriptionPlans:get",
    SEARCH_FEATURES = "features:search",
    GET_FEATURE_CATALOG_CONFIG = "subscriptionPlans:get",
    GET_FEATURE_PLAN_USAGE = "subscriptionPlans:get",

    // Check #
    UPDATE_CHECK = "smartdining:checks:updateCheck",
    ADD_CASH_PAYMENT = "smartdining:checks:addCashPayment",
    ADD_CARD_PAYMENT = "smartdining:checks:addCardPayment",
    MAKE_CHECK_PREPARING = "smartdining:checks:makeCheckPreparing",
    MAKE_CHECK_READY = "smartdining:checks:makeCheckReady",
    MAKE_SELECTION_READY = "smartdining:checks:makeSelectionReady",
    MAKE_SELECTION_PREPARING = "smartdining:checks:makeSelectionPreparing",
    CANCEL_CHECK = "smartdining:checks:cancelCheck",
    ADD_CHECK = "smartdining:checks:addCheck",
    GET_CHECK_DETAILS = "smartdining:checks:getCheckDetails",
    CAPTURE_CHECK_PAYMENT = "smartdining:checks:captureCheckPayment",
    GET_CHECKS = "smartdining:checks:getChecks",
    GET_PAID_PAYMENT_CHECKS = "smartdining:checks:getPaidChecks",
    GET_DELETE_CHECKS = "smartdining:checks:getDeleteChecks",
    GET_CANCEL_CHECKS = "smartdining:checks:getCancelChecks",
    GET_CLOSED_CHECKS = "smartdining:checks:getClosedChecks",
    VOID_CHECK_PAYMENT = "smartdining:checks:voidPayment",
    CANCEL_CHECK_PAYMENT = "smartdining:checks:cancelPayment",

    // Payment Settings #
    SET_PAYMENT_SETTINGS = "smartdining:payments:setPaymentSettings",
    VOID_PAYMENT = "smartdining:payments:voidPayment",
    CREATE_PAYMENT_INTENT = "smartdining:payments:createPaymentIntent",
    CREATE_PAYMENT_INTENT_FOR_ORDER = "smartdining:payments:createPaymentIntentForOrder",
    PROCESS_CHECK_OUT = "smartdining:payments:processCheckOut",
    CREATE_PAYMENT_INTENT_FOR = "smartdining:payments:createPaymentIntentFor",
    CREATE_CARD_PAYMENT_INTENT = "smartdining:payments:createCardPaymentIntent",
    CONFIRM_PAYMENT_INTENT = "smartdining:payments:confirmPaymentIntent",
    CANCEL_PAYMENT_INTENT = "smartdining:payments:cancelPaymentIntent",
    GET_PAYMENT_METHODS = "smartdining:payments:getPaymentMethods",
    CREATE_PAYMENT_METHOD = "smartdining:payments:createPaymentMethod",
    CAPTURE_PAYMENT_METHOD = "smartdining:payments:capturePaymentMethod",
    DETACH_PAYMENT_METHOD = "smartdining:payments:detachPaymentMethod",
    CREATE_SETUP_PAYMENT_INTENT = "smartdining:payments:createSetupPaymentIntent",
    CREATE_SETUP_INTENT_FOR_ORDER = "smartdining:payments:createSetupIntentForOrder",
    GET_SETUP_PAYMENT_INTENTS = "smartdining:payments:getSetupPaymentIntents",
    CANCEL_SETUP_PAYMENT_INTENT = "smartdining:payments:cancelSetupPaymentIntent",
    CONFIRM_SETUP_PAYMENT_INTENT = "smartdining:payments:confirmSetupPaymentIntent",
    CAPTURE_PAYMENT_INTENT = "smartdining:payments:capturepaymentintent",
    UPDATE_PAYMENT_INTENT = "smartdining:payments:updatePaymentIntent",
    CREATE_ACCOUNT_CONNECT = "smartdining:payments:createAccountConnect",
    PAYOUT_ACCOUNT_CONNECT = "smartdining:payments:payoutAccountConnect",
    GET_ACCOUNT_CONNECT = "smartdining:payments:getAccountConnect",
    UPDATE_ACCOUNT_CONNECT = "smartdining:payments:updateAccountConnect",
    DELETE_ACCOUNT_CONNECT = "smartdining:payments:deleteAccountConnect",
    CANCEL_PAYMENT_SUBSCRIPTION = "smartdining:payments:cancelSubscription",
    UPDATE_INVOICE_PAYMENT_INTENT = "smartdining:payments:updateInvoicePaymentIntent",
    UPDATE_PAYMENT_CUSTOMER = "smartdining:payments:updateCustomer",
    GET_STRIPE_CUSTOMER = "smartdining:payments:getStripeCustomer",
    GET_CHARGES_BY_ADMIN = "smartdining:payments:getChargesByAdmin",
    GET_INVOICE = "smartdining:payments:getInvoice",
    CHECK_INVOICE_PAYMENT = "smartdining:payments:checkInvoicePayment",
    SMART_DINING_WEBHOOK = "smartdining:payments:webhook/stripe",

    // Role #
    /**
     * Umbrella action for role lifecycle routes whose fine-grained access is expressed via
     * `iamPolicyDocument` **Resource** (and optional Deny). Used e.g. for restore-default and restore-soft-deleted.
     * Prefer this over legacy `RESTORE_ROLE_DELETED` / `RESTORE_ROLE_TO_DEFAULT` on new policies.
     */
    ROLE = "smartdining:roles:role",
    RESTORE_ROLE_DELETED = "smartdining:roles:restoreRoleDeleted",
    SET_ROLE_PERMISSION = "smartdining:roles:addRolePermission",
    CREATE_ROLE = "smartdining:roles:createRole",
    UPDATE_ROLE = "smartdining:roles:updateRole",
    DELETE_ROLE = "smartdining:roles:deleteRole",

    // Roles

    GET_ROLE_DETAILS = "smartdining:roles:getRoleDetails",
    GET_USER_PERMISSIONS = "smartdining:roles:getUserPermissions",
    /**
     * Bulk export of operational data (CSV/XLSX and API export endpoints). Super users receive every
     * `RESOURCE_PERMISSIONS` value at login; other roles need this action in IAM (`smartdining:platform:exportAllData`
     * or `platform:exportAllData` after prefix strip) to use export UI.
     */
    PLATFORM_EXPORT_ALL_DATA = "smartdining:platform:exportAllData",
    GET_RESTAURANT_ROLES = "smartdining:roles:getRestaurantRoles",
    /** Super-user tenant IAM ceilings (global templates; no subscription cap on template rows). */
    LIST_TENANT_ROLE_TEMPLATES = "smartdining:roles:admins:listTenantRoleTemplates",
    GET_TENANT_ROLE_TEMPLATE = "smartdining:roles:admins:getTenantRoleTemplate",
    UPDATE_TENANT_ROLE_TEMPLATE = "smartdining:roles:admins:updateTenantRoleTemplate",
    RESET_TENANT_ROLE_TEMPLATE_FROM_CODE = "smartdining:roles:admins:resetTenantRoleTemplateFromCode",
    RESTORE_ROLE_TO_DEFAULT = "smartdining:roles:restoreRoleToDefault",
    GET_RESTAURANT_DELETED_ROLES = "smartdining:roles:getRestaurantDeletedRoles",
    REMOVE_ROLE_PERMISSION = "smartdining:roles:removeRolePermission",

    // Discount #
    CREATE_DISCOUNT = "smartdining:discounts:createDiscount",
    APPLY_PERCENT = "smartdining:discounts:applyPercent",
    VOID_PERCENT = "smartdining:discounts:voidPercent",
    RESTORE_DISCOUNT = "smartdining:discounts:restoreDiscount",
    UPDATE_DISCOUNT = "smartdining:discounts:updateDiscount",
    GET_DISCOUNT_DETAIL = "smartdining:discounts:getDiscountDetail",
    DELETE_DISCOUNT = "smartdining:discounts:deleteDiscount",

    // Sales leads (REST resource under /api/v1/sales-leads)
    GET_DELETE_SALES_LEADS = "smartdining:sales-leads:archived",
    GET_SALES_LEADS = "smartdining:sales-leads:default",
    GET_SALES_LEAD = "smartdining:sales-leads::id",
    UPDATE_SALES_LEAD = "smartdining:sales-leads::id",
    DELETE_SALES_LEAD = "smartdining:sales-leads::id",
    SALES_LEADS_CREATE_SALES_LEAD = "smartdining:sales-leads:default",
    SALES_LEADS_GET_SALES_LEADS = "smartdining:sales-leads:default",

    // Company #
    CREATE_COMPANY = "smartdining:organizations:createCompany",
    GET_COMPANY = "smartdining:organizations:getCompanyDetails",
    UPDATE_COMPANY = "smartdining:organizations:updateCompany",
    DELETE_COMPANY = "smartdining:organizations:deleteCompany",
    DISABLE_COMPANY = "smartdining:organizations:disableCompany",
    ENABLE_COMPANY = "smartdining:organizations:enableCompany",
    Add_ORGANIZATION_ACCOUNT = "Add_ORGANIZATION_ACCOUNT",
    // Contactus #
    GET_CONTACT_US = "smartdining:contactUs:getContactUs",

    // Course #
    UPDATE_COURSE = "smartdining:courses:updateCourse",
    CREATE_COURSE = "smartdining:courses:createCourse",
    DELETE_COURSE = "smartdining:courses:deleteCourse",

    // Customer Feedback #
    GET_CUSTOMER_FEED_BACK_RESULT = "smartdining:customerFeedback:getCustomerFeedBackResult",
    CUSTOMER_FEEDBACK_CREATE_CUSTOMER_FEEDBACK = "smartdining:customerFeedback:createCustomerFeedback",
    GET_CUSTOMER_FEEDBACKS = "smartdining:customerFeedback:getCustomerFeedBacks",

    // Guest Management #
    CREATE_GUEST_MANAGEMENT = "smartdining:guestManagements:createGuestManagement",
    UPDATE_GUEST_MANAGEMENT = "smartdining:guestManagements:updateGuestManagement",

    // Pre Station #
    DELETE_PRE_STATION = "smartdining:prepStations:deletePrepStation",
    UPDATE_PRE_STATION = "smartdining:prepStations:updatePrepStation",
    CREATE_PRE_STATION = "smartdining:prepStations:createPrepStation",
    PREP_STATIONS_GET_PREP_STATIONS = "smartdining:prepStations:getPrepStations",

    // Printer #
    DELETE_PRINTER = "smartdining:printers:deletePrinter",
    READ_PRINTER = "smartdining:printers:readPrinter",
    UPDATE_PRINTER = "smartdining:printers:updatePrinter",
    CREATE_PRINTER = "smartdining:printers:createPrinter",

    // Promotional Code #
    RESTORE_PROMOTIONAL_CODE = "smartdining:promotionalCodes:restorePromotionalCode",
    UPDATE_PROMOTIONAL_CODE = "smartdining:promotionalCodes:updatePromotionalCode",
    ADD_PROMOTIONAL_CODE = "smartdining:promotionalCodes:addPromotionalCode",
    DELETE_PROMOTIONAL_CODE = "smartdining:promotionalCodes:deletePromotionalCode",
    GET_PROMOTIONAL_CODE = "smartdining:promotionalCodes:getPromotionalCode",

    // Department #
    UPDATE_DEPARTMENT = "smartdining:departments:updateDepartment",
    CREATE_DEPARTMENT = "smartdining:departments:createDepartment",
    DELETE_DEPARTMENT = "smartdining:departments:deleteDepartment",
    GET_DEPARTMENTS = "smartdining:departments:getDepartments",

    // Sale Categories #
    CREATE_SALE_CATEGORY = "smartdining:saleCategories:createSaleCategory",
    UPDATE_SALE_CATEGORY = "smartdining:saleCategories:updateSaleCategory",
    DELETE_SALE_CATEGORY = "smartdining:saleCategories:deleteSaleCategory",
    SALE_CATEGORIES_GET_SALE_CATEGORIES = "smartdining:saleCategories:getSaleCategories",

    // Table Combinations #
    BLOCK_OR_UN_BLOCK_TABLE = "smartdining:tables:blockOrUnBlockTable",
    CREATE_TABLE_COMBINATION = "smartdining:tableCombinations:createTableCombination",
    CREATE_BULK_TABLE_COMBINATION = "smartdining:tableCombinations:createBulkTableCombinations",
    UPDATE_TABLE_COMBINATION = "smartdining:tableCombinations:updateTableCombination",
    UPDATE_BULK_TABLE_COMBINATION = "smartdining:tableCombinations:updateBulkTableCombinations",
    DELETE_TABLE_COMBINATION = "smartdining:tableCombinations:deleteTableCombination",

    // Table Setting Divider #
    CREATE_BULK_ROOM_DIVIDER = "smartdining:tableSettingDividers:createBulkRoomDivider",
    CREATE_ROOM_DIVIDER = "smartdining:tableSettingDividers:createRoomDivider",
    UPDATE_ROOM_DIVIDER = "smartdining:tableSettingDividers:updateRoomDivider",
    DELETE_ROOM_DIVIDER = "smartdining:tableSettingDividers:deleteRoomDivider",
    CREATE_ROOM_DIVIDER_NEW = "smartdining:tableSettingDividers:createRoomDivider",
    CREATE_BULK_ROOM_DIVIDER_NEW = "smartdining:tableSettingDividers:createBulkRoomDivider",
    UPDATE_ROOM_DIVIDER_NEW = "smartdining:tableSettingDividers:updateRoomDivider",
    UPDATE_BULK_ROOM_DIVIDER = "smartdining:tableSettingDividers:updateBulkRoomDivider",
    GET_BULK_ROOM_DIVIDER_BY_ROOM = "smartdining:tableSettingDividers:getBulkRoomDividerByRoom",
    GET_BULK_ROOM_DIVIDER_BY_RESTAURANT = "smartdining:tableSettingDividers:getBulkRoomDividerByRestaurantId",
    DELETE_ROOM_DIVIDER_NEW = "smartdining:tableSettingDividers:deleteRoomDivider",


    // Auth Routes #
    SIGN_UP = "smartdining:auth:signUp",
    SIGN_IN = "smartdining:auth:signIn",
    REFRESH_TOKEN = "smartdining:auth:refreshToken",
    GET_AUTHENTICATED_USER = "smartdining:auth:getAuthenticatedUser",
    SIGN_OUT = "smartdining:auth:signOut",
    CHECK_PASSWORD = "smartdining:auth:checkPassword",
    SETUP_MFA_TIME_BASED_OTP = "smartdining:auth:setupMFATimeBasedOTP",
    SET_PREFERRED_MFA = "smartdining:auth:setPreferredMFA",
    GET_PREFERRED_MFA = "smartdining:auth:getPreferredMFA",
    RESEND_EMAIL_SIGN_UP = "smartdining:auth:resendEmailSignUp",
    RESEND_PHONE_SIGN_UP = "smartdining:auth:resendPhoneSignUp",
    CONFIRM_EMAIL_SIGN_UP = "smartdining:auth:confirmEmailSignUp",
    SEND_CONFIRM_CODE = "smartdining:auth:sendConfirmCode",
    CONFIRM_USER_CODE = "smartdining:auth:confirmUserCode",
    SEND_GUEST_CONFIRM_CODE = "smartdining:auth:sendGuestConfirmCode",
    CONFIRM_GUEST_CODE = "smartdining:auth:confirmGuestCode",
    CONFIRM_PHONE_SIGN_UP = "smartdining:auth:confirmPhoneSignUp",
    FORGET_PASSWORD = "smartdining:auth:forgetPassword",
    RESET_PASSWORD = "smartdining:auth:resetPassword",
    CHANGE_PASSWORD = "smartdining:auth:changePassword",
    CHANGE_USER_EMAIL = "smartdining:auth:changeUserEmail",
    CHANGE_USER_PHONE_NUMBER = "smartdining:auth:changeUserPhoneNumber",

    //  Organization Routes #
    ADD_COMPANY_ADDRESS = "smartdining:organization:addAddress",
    GET_COMPANY_ADDRESS = "smartdining:organization:getAddress",
    DELETE_COMPANY_ADDRESS = "smartdining:organization:deleteAddress",
    UPDATE_COMPANY_ADDRESS = "smartdining:organization:updateAddress",
    UPLOAD_COMPANY_IMAGE = "smartdining:organization:uploadImage",
    DELETE_COMPANY_IMAGE = "smartdining:organization:deleteImage",
    SEARCH_COMPANY_USERS = "smartdining:organization:searchUsers",
    GET_COMPANY_USER_BY_PHONE_OR_EMAIL = "smartdining:organization:getUserByPhoneOrEmail",
    ENABLE_COMPANY_USER_MFA = "smartdining:organization:enableUserMFA",
    DISABLE_COMPANY_USER_MFA = "smartdining:organization:disableUserMFA",
    GET_COMPANY_USER = "smartdining:organization:getUser",
    DISABLE_COMPANY_USER = "smartdining:organization:disableUser",
    UPDATE_COMPANY_USER_INFO_BY_MANAGER = "smartdining:organization:updateUserInfoByManager",
    UPDATE_COMPANY_USER = "smartdining:organization:updateUser",
    CHANGE_COMPANY_USER_PASSWORD = "smartdining:organization:changeUserPassword",
    DELETE_COMPANY_USER = "smartdining:organization:deleteUser",
    GET_COMPANY_DETAILS = "smartdining:organizations:getCompanyDetails",
    GET_COMPANY_RESTAURANT_COUNT = "smartdining:organizations:getCompanyRestaurantCount",
    GET_COMPANY_LIST = "smartdining:organizations:getCompanyList",
    GET_COMPANIES = "smartdining:organizations:getCompanies",
    GET_COMPANY_ACCOUNT_RESTAURANTS = "smartdining:organizations:getAccountRestaurants",
    ASSIGN_MANAGER = "smartdining:organizations:assignManager",

    // Admin Routes #
    ADMIN_SIGN_UP = "smartdining:auth:admins/signUp",
    ADMIN_SIGN_IN = "smartdining:auth:admins/signIn",
    ADMIN_REFRESH_TOKEN = "smartdining:auth:admins/refreshToken",
    ADMIN_CHANGE_USER_EMAIL = "smartdining:auth:admins/changeUserEmail",
    ADMIN_CHANGE_USER_PHONE_NUMBER = "smartdining:auth:admins/changeUserPhoneNumber",
    ADMIN_GET_AUTHENTICATED_USER = "smartdining:auth:admins/getAuthenticatedUser",
    ADMIN_SIGN_OUT = "smartdining:auth:admins/signOut",
    ADMIN_CHECK_PASSWORD = "smartdining:auth:admins/checkPassword",
    ADMIN_SETUP_MFA_TIME_BASED_OTP = "smartdining:auth:admins/setupMFATimeBasedOTP",
    ADMIN_SET_PREFERRED_MFA = "smartdining:auth:admins/setPreferredMFA",
    ADMIN_GET_PREFERRED_MFA = "smartdining:auth:admins/getPreferredMFA",
    ADMIN_RESEND_EMAIL_SIGN_UP = "smartdining:auth:admins/resendEmailSignUp",
    ADMIN_RESEND_PHONE_SIGN_UP = "smartdining:auth:admins/resendPhoneSignUp",
    ADMIN_CONFIRM_EMAIL_SIGN_UP = "smartdining:auth:admins/confirmEmailSignUp",
    ADMIN_SEND_CONFIRM_CODE = "smartdining:auth:admins/sendConfirmCode",
    ADMIN_CONFIRM_USER_CODE = "smartdining:auth:admins/confirmUserCode",
    ADMIN_SEND_GUEST_CONFIRM_CODE = "smartdining:auth:admins/sendGuestConfirmCode",
    ADMIN_CONFIRM_GUEST_CODE = "smartdining:auth:admins/confirmGuestCode",
    ADMIN_CONFIRM_PHONE_SIGN_UP = "smartdining:auth:admins/confirmPhoneSignUp",
    ADMIN_FORGET_PASSWORD = "smartdining:auth:admins/forgetPassword",
    ADMIN_RESET_PASSWORD = "smartdining:auth:admins/resetPassword",
    ADMIN_CHANGE_PASSWORD = "smartdining:auth:admins/changePassword",
    ADMIN_ADD_ADDRESS = "smartdining:admins:addAddress",
    ADMIN_GET_ADDRESS = "smartdining:admins:getAddress",
    ADMIN_DELETE_ADDRESS = "smartdining:admins:deleteAddress",
    ADMIN_UPDATE_ADDRESS = "smartdining:admins:updateAddress",
    ADMIN_UPLOAD_IMAGE = "smartdining:admins:uploadImage",
    ADMIN_DELETE_IMAGE = "smartdining:admins:deleteImage",
    ADMIN_SEARCH_USERS = "smartdining:admins:searchUsers",
    ADMIN_GET_USER_BY_PHONE_OR_EMAIL = "smartdining:admins:getUserByPhoneOrEmail",
    ADMIN_ENABLE_USER_MFA = "smartdining:admins:enableUserMFA",
    ADMIN_DISABLE_USER_MFA = "smartdining:admins:disableUserMFA",
    ADMIN_GET_USER = "smartdining:admins:getUser",
    ADMIN_DISABLE_USER = "smartdining:admins:disableUser",
    ADMIN_UPDATE_USER_INFO_BY_MANAGER = "smartdining:admins:updateUserInfoByManager",
    ADMIN_UPDATE_USER = "smartdining:admins:updateUser",
    ADMIN_CHANGE_USER_PASSWORD = "smartdining:admins:changeUserPassword",
    ADMIN_DELETE_USER = "smartdining:admins:deleteUser",

    // User Routes #
    USERS_ADD_ADDRESS = "smartdining:users:addAddress",
    //GET_USER_POINT_REPORT = "/users/getUserPointReport",
    USERS_GET_ADDRESS = "smartdining:users:getAddress",
    USERS_DELETE_ADDRESS = "smartdining:users:deleteAddress",
    USERS_UPDATE_ADDRESS = "smartdining:users:updateAddress",
    USERS_UPLOAD_IMAGE = "smartdining:users:uploadImage",
    USERS_DELETE_IMAGE = "smartdining:users:deleteImage",
    USERS_SEARCH_USERS = "smartdining:users:searchUsers",
    USERS_GET_USER_BY_PHONE_OR_EMAIL = "smartdining:users:getUserByPhoneOrEmail",
    USERS_ENABLE_USER_MFA = "smartdining:users:enableUserMFA",
    USERS_DISABLE_USER_MFA = "smartdining:users:disableUserMFA",
    USERS_GET_USER = "smartdining:users:getUser",
    USERS_DISABLE_USER = "smartdining:users:disableUser",
    USERS_UPDATE_USER_INFO_BY_MANAGER = "smartdining:users:updateUserInfoByManager",
    USERS_UPDATE_USER = "smartdining:users:updateUser",
    USERS_CHANGE_USER_PASSWORD = "smartdining:users:changeUserPassword",
    USERS_DELETE_USER = "smartdining:users:deleteUser",
    USERS_UPDATE_DIETARY_RESTRICTIONS = "smartdining:users:updateDietaryRestrictions",
    USERS_ADD_DIETARY_RESTRICTION = "smartdining:users:addDietaryRestriction",
    USERS_ADD_DIETARY_RESTRICTIONS = "smartdining:users:addDietaryRestrictions",
    USERS_REMOVE_DIETARY_RESTRICTION = "smartdining:users:removeDietaryRestriction",
    USERS_GET_DIETARY_RESTRICTIONS = "smartdining:users:getDietaryRestrictions",

    // Role Routes #
    ROLES_CREATE_ROLE = "smartdining:roles:createRole",
    ROLES_GET_ROLE_DETAILS = "smartdining:roles:getRoleDetails",
    ROLES_GET_USER_PERMISSIONS = "smartdining:roles:getUserPermissions",
    ROLES_UPDATE_ROLE = "smartdining:roles:updateRole",
    ROLES_ASSIGN_EMPLOYEE_ROLE = "smartdining:roles:assignEmployeeRole",
    ROLES_GET_RESTAURANT_ROLES = "smartdining:roles:getRestaurantRoles",
    ROLES_LIST_TENANT_ROLE_TEMPLATES = "smartdining:roles:admins:listTenantRoleTemplates",
    ROLES_GET_TENANT_ROLE_TEMPLATE = "smartdining:roles:admins:getTenantRoleTemplate",
    ROLES_UPDATE_TENANT_ROLE_TEMPLATE = "smartdining:roles:admins:updateTenantRoleTemplate",
    ROLES_RESET_TENANT_ROLE_TEMPLATE_FROM_CODE = "smartdining:roles:admins:resetTenantRoleTemplateFromCode",
    ROLES_RESTORE_ROLE_TO_DEFAULT = "smartdining:roles:restoreRoleToDefault",
    ROLES_DELETE_ROLE = "smartdining:roles:deleteRole",
    ROLES_GET_RESTAURANT_DELETED_ROLES = "smartdining:roles:getRestaurantDeletedRoles",
    /** Same action string as `RESOURCE_PERMISSIONS.ROLE`; alias for `ROLES_*` naming style. */
    ROLES_ROLE = "smartdining:roles:role",
    ROLES_RESTORE_ROLE_DELETED = "smartdining:roles:restoreRoleDeleted",
    ROLES_ADD_ROLE_PERMISSION = "smartdining:roles:addRolePermission",
    ROLES_REMOVE_ROLE_PERMISSION = "smartdining:roles:removeRolePermission",

    // Miscellaneous
    GET_USER_CONNECTION = "smartdining:getUserConnection:default",

    // Google reserve
    GOOGLE_UPDATE_MERCHANT = "smartdining:googleReserve:googleUpdateMerchant",
    GOOGLE_SERVICE_CREATE = "smartdining:googleReserve:googleServiceCreate",
    GOOGLE_SERVICE_UPDATE = "smartdining:googleReserve:googleServiceUpdate",
    GOOGLE_BOOKING_UPDATE = "smartdining:googleReserve:googleBookingUpdate",
    GOOGLE_DELETE_MERCHANT = "smartdining:googleReserve:deleteMerchant",
    GOOGLE_CANCEL_BOOKING = "smartdining:googleReserve:cancelBooking",
    GOOGLE_HEALTH_CHECK = "smartdining:googleReserve:v3/HealthCheck",
    GOOGLE_BATCH_AVAILABILITY_LOOKUP = "smartdining:googleReserve:v3/batchAvailabilityLookup",
    GOOGLE_CHECK_AVAILABILITY = "smartdining:googleReserve:v3/checkAvailability",
    GOOGLE_CREATE_BOOKING = "smartdining:googleReserve:v3/CreateBooking",
    GOOGLE_CREATE_WAITLIST_ENTRY = "smartdining:googleReserve:v3/CreateWaitlistEntry",
    GOOGLE_BATCH_GET_WAIT_ESTIMATES = "smartdining:googleReserve:v3/BatchGetWaitEstimates",
    GOOGLE_GET_WAITLIST_ENTRY = "smartdining:googleReserve:v3/GetWaitlistEntry",
    GOOGLE_DELETE_WAITLIST_ENTRY = "smartdining:googleReserve:v3/DeleteWaitlistEntry",
    GOOGLE_UPDATE_BOOKING = "smartdining:googleReserve:v3/updateBooking",
    GOOGLE_GET_BOOKING_STATUS = "smartdining:googleReserve:v3/getBookingStatus",
    GOOGLE_LIST_BOOKINGS = "smartdining:googleReserve:v3/ListBookings",
    GOOGLE_CHECK_ORDER_FULFILL_AVAILABILITY = "smartdining:googleReserve:v3/checkOrderFulfillAvailability",
    GOOGLE_GET_GOOGLE_MERCHANTS = "smartdining:googleReserve:v3/getGoogleMerchants",
    GOOGLE_GET_GOOGLE_AVAILABILITIES = "smartdining:googleReserve:v3/getGoogleAvailabilities",
    GOOGLE_GET_GOOGLE_AVAILABILITY = "smartdining:googleReserve:v3/getGoogleAvailability",
    GOOGLE_GET_GOOGLE_SERVICE = "smartdining:googleReserve:v3/getGoogleService",

    // Favorites
    FAVORITES_CREATE_FAVORITE = "smartdining:favorites:createFavorite",
    GET_FAVORITE = "smartdining:favorites:getFavorite",
    GET_FAVORITE_BY_RESTAURANT = "smartdining:favorites:getFavoriteByRestaurant",
    GET_LIST_FAVORITE = "smartdining:favorites:getListFavorite",
    DELETE_FAVORITE = "smartdining:favorites:deleteFavorite",

    // Reviews
    REVIEWS_CREATE_REVIEW = "smartdining:reviews:createReview",
    GET_REVIEW_DETAILS = "smartdining:reviews:getReviewDetails",
    GET_REVIEW_BY_RESTAURANT = "smartdining:reviews:getReviewByRestaurant",
    GET_LIST_REVIEWS = "smartdining:reviews:getListReviews",
    GET_REVIEW_REPORT = "smartdining:reviews:getReviewReport",
    GET_REVIEW_REPORT_BY_RATING = "smartdining:reviews:getReviewReportByRating",
    DELETE_REVIEW = "smartdining:reviews:deleteReview",
    REPLY_TO_CUSTOMER_REVIEW = "smartdining:legacy:reply_to_customer_review",


    // guest Managements
    GUEST_MANAGEMENT_GET_GUEST_MANAGEMENT_BY_RESTAURANT = "smartdining:guestManagements:getGuestManagementByRestaurant",
    GUEST_MANAGEMENT_GET_GUEST_MANAGEMENT_BY_COMPANY_UID = "smartdining:guestManagements:getGuestManagementByCompanyUID",
    GUEST_MANAGEMENT_GET_GUEST_REVIEW_SETTINGS = "smartdining:guestManagements:getGuestReviewSettings",


    COURSES_GET_COURSES = "smartdining:courses:getCourses",

    CONTACT_US_ADD_CONTACT_US = "smartdining:contactUs:addContactUs",


    REPORTS_GET_REPORTS = "smartdining:reports:getReports",

    LOYALTY_PROGRAMS_SUBSCRIBE_TO_PROGRAM = "smartdining:loyaltyPrograms:subscribeToProgram",
    LOYALTY_PROGRAMS_GET_LOYALTY_PROGRAM = "smartdining:loyaltyPrograms:getLoyaltyProgram",
    LOYALTY_PROGRAMS_GET_USER_LOYALTY_PROGRAM = "smartdining:loyaltyPrograms:getUserLoyaltyProgram",
    LOYALTY_PROGRAMS_UNSUBSCRIBE_PROGRAM = "smartdining:loyaltyPrograms:unSubscribeProgram",

    LOYALTY_SETTINGS_CREATE_LOYALTY_SETTINGS = "smartdining:loyaltySettings:createLoyaltySettings",
    LOYALTY_SETTINGS_UPDATE_LOYALTY_SETTINGS = "smartdining:loyaltySettings:updateLoyaltySettings",
    LOYALTY_SETTINGS_RESTORE_LOYALTY_SETTINGS = "smartdining:loyaltySettings:restoreLoyaltySettings",
    LOYALTY_SETTINGS_GET_LOYALTY_SETTINGS = "smartdining:loyaltySettings:getLoyaltySettings",
    LOYALTY_SETTINGS_GET_LOYALTY_SETTINGS_DETAILS = "smartdining:loyaltySettings:getLoyaltySettingsDetails",
    LOYALTY_SETTINGS_DELETE_LOYALTY_SETTINGS = "smartdining:loyaltySettings:deleteLoyaltySettings",
    LOYALTY_SETTINGS_CREATE_REWARD = "smartdining:loyaltySettings:createReward",
    LOYALTY_SETTINGS_GET_REWARDS = "smartdining:loyaltySettings:getRewards",
    LOYALTY_SETTINGS_SUGGEST_REWARDS = "smartdining:loyaltySettings:suggestRewards",
    LOYALTY_SETTINGS_APPLY_REWARD = "smartdining:loyaltySettings:applyReward",
    LOYALTY_SETTINGS_GET_REWARD_DETAILS = "smartdining:loyaltySettings:getRewardDetails",
    LOYALTY_SETTINGS_UPDATE_REWARD = "smartdining:loyaltySettings:updateReward",
    LOYALTY_SETTINGS_DELETE_REWARD = "smartdining:loyaltySettings:deleteReward",

    APPLY_DISCOUNT = "smartdining:legacy:apply_discount",
    VIEW_DISCOUNT = "smartdining:legacy:view_discount",


    PROMOTIONAL_CODES_GET_PROMOTIONAL_CODES = "smartdining:promotionalCodes:getPromotionalCodes",
    PROMOTIONAL_CODES_APPLY_PROMOTIONAL_CODE = "smartdining:promotionalCodes:applyPromotionalCode",
    PROMOTIONAL_CODES_GET_ACTIVE_PROMOTIONAL_CODES = "smartdining:promotionalCodes:getActivePromotionalCodes",
    PROMOTIONAL_CODES_GET_DELETE_PROMOTIONAL_CODES = "smartdining:promotionalCodes:getDeletePromotionalCodes",
    PROMOTIONAL_CODES_GET_PROMOTIONAL_CODE_DETAIL = "smartdining:promotionalCodes:getPromotionalCodeDetail",
    DISABLE_PROMOTIONAL_CODE = "smartdining:legacy:disable_promotional_code",
    ENABLE_PROMOTIONAL_CODE = "smartdining:legacy:enable_promotional_code",

    SURVEYS_SUBMIT_CUSTOMER_SURVEY = "smartdining:surveys:submitCustomerSurvey",
    SURVEYS_ADD_CUSTOMER_SURVEY_QUESTION = "smartdining:surveys:addCustomerSurveyQuestion",
    SURVEYS_SET_ORDER_SURVEY_STATUS = "smartdining:surveys:setOrderSurveyStatus",
    SURVEYS_SET_BOOKING_SURVEY_STATUS = "smartdining:surveys:setBookingSurveyStatus",
    SURVEYS_GET_SURVEY_DETAILS = "smartdining:surveys:getSurveyDetails",
    SURVEYS_SEARCH_CUSTOMER_SURVEYS = "smartdining:surveys:searchCustomerSurveys",
    SURVEYS_REPORT_SURVEYS_OVERALL_SCORES = "smartdining:surveys:reportSurveysOverallScores",
    SURVEYS_REPORT_SURVEY_QUESTIONS_SCORES = "smartdining:surveys:reportSurveyQuestionsScores",
    SURVEYS_REPORT_SURVEY_SERVERS_SCORES = "smartdining:surveys:reportSurveyServersScores",
    SURVEYS_ADD_BOOKING_SURVEY_QUESTION = "smartdining:surveys:addBookingSurveyQuestion",
    SURVEYS_ADD_ORDER_SURVEY_QUESTION = "smartdining:surveys:addOrderSurveyQuestion",
    SURVEYS_GET_BOOKING_SURVEY_QUESTIONS = "smartdining:surveys:getBookingSurveyQuestions",
    SURVEYS_GET_ORDER_SURVEY_QUESTIONS = "smartdining:surveys:getOrderSurveyQuestions",
    SURVEYS_UPDATE_BOOKING_SURVEY_QUESTIONS = "smartdining:surveys:updateBookingSurveyQuestions",
    SURVEYS_UPDATE_ORDER_SURVEY_QUESTIONS = "smartdining:surveys:updateOrderSurveyQuestions",
    SURVEYS_GET_RESTAURANT_SURVEYS = "smartdining:surveys:getRestaurantSurveys",
    SURVEYS_GET_OPTIONAL_SURVEYS = "smartdining:surveys:getOptionalSurveys",
    SURVEYS_GET_CUSTOMER_RESTAURANT_SURVEYS = "smartdining:surveys:getCustomerRestaurantSurveys",
    SURVEYS_GET_RESTAURANT_SURVEY_REPORT = "smartdining:surveys:getRestaurantSurveyReport",
    SURVEYS_GET_GUEST_SENTIMENTS = "smartdining:surveys:getGuestSentiments",
    SURVEYS_GET_PERFORMANCE_OVER_TIME = "smartdining:surveys:getPerformanceOverTime",
    SURVEYS_GET_SERVER_SENTIMENTS = "smartdining:surveys:getServerSentiments",


    // USER_NOTIFICATIONS
    USER_NOTIFICATIONS_SUBSCRIBE = "smartdining:userNotifications:subscribe",
    USER_NOTIFICATIONS_UNSUBSCRIBE = "smartdining:userNotifications:unsubscribe",
    USER_NOTIFICATIONS_PUBLISH = "smartdining:userNotifications:publish",

    RESTAURANT_NOTIFICATIONS_SUBSCRIBE = "smartdining:restaurantNotifications:subscribe",
    RESTAURANT_NOTIFICATIONS_UNSUBSCRIBE = "smartdining:restaurantNotifications:unsubscribe",
    RESTAURANT_NOTIFICATIONS_PUBLISH = "smartdining:restaurantNotifications:publish",
    RESTAURANT_NOTIFICATIONS_2WAYS_MESSAGE = "smartdining:restaurantNotifications:2waysMessage",
    RESTAURANT_NOTIFICATIONS_2WAYS_MESSAGE_LISTEN = "smartdining:restaurantNotifications:2waysMessageListen",

    PUSH_NOTIFICATIONS_SUBSCRIBE_RESTAURANT_DEVICE = "smartdining:PushNotifications:subscribeRestaurantDevice",
    PUSH_NOTIFICATIONS_UNSUBSCRIBE_RESTAURANT_DEVICE = "smartdining:PushNotifications:unsubscribeRestaurantDevice",
    PUSH_NOTIFICATIONS_PUBLISH_TO_TOPICS = "smartdining:PushNotifications:publishToTopics",

    // INTEGRATIONS
    INTEGRATIONS_SET_RESTAURANT_SETTING_FROM_EXCEL = "smartdining:integrations:setRestaurantSettingFromExcel",
    INTEGRATIONS_CONVERT_TO_SMART_DINING_MODIFIER_GROUP = "smartdining:integrations:convertToSmartDiningModifierGroup",
    INTEGRATIONS_CONVERT_TO_SMART_DINING_MENU = "smartdining:integrations:convertToSmartDiningMenu",
    INTEGRATIONS_LOAD_RESTAURANT_FROM_TOAST = "smartdining:integrations:loadRestaurantFromToast",

    // Social Media
    GET_REDIRECT_URL = "smartdining:socialmedias:getRedirectUrl",
    GET_SOCIAL_MEDIA_CONNECTIONS = "smartdining:socialmedias:getSocialMediaConnections",
    DELETE_CONNECTION = "smartdining:socialmedias:deleteConnection",
    GET_CONNECTION = "smartdining:socialmedias:getConnection",
    SAVE_CONNECTION = "smartdining:socialmedias:saveConnection",
    CREATE_POST = "smartdining:socialmedias:createPost",
    GET_SOCIAL_SCHEDULE_POSTS = "smartdining:socialmedias:getSocialSchedulePosts",
    LINKEDIN_GET_LINKEDIN_TOKENS = "smartdining:socialmedias:linkedin/getLinkedinTokens",
    LINKEDIN_GET_LINKEDIN_USER_INFO = "smartdining:socialmedias:linkedin/getLinkedinUserInfo",
    LINKEDIN_GET_LINKEDIN_ORGANIZATIONS = "smartdining:socialmedias:linkedin/getLinkedinOrganizations",
    LINKEDIN_GET_LINKEDIN_POSTS = "smartdining:socialmedias:linkedin/getLinkedinPosts",
    LINKEDIN_GET_LINKEDIN_POST_DETAILS = "smartdining:socialmedias:linkedin/getLinkedinPostDetails",
    LINKEDIN_GET_REFRESH_LINKEDIN_TOKEN = "smartdining:socialmedias:linkedin/getRefreshLinkedinToken",
    LINKEDIN_GET_ORGANIZATION_SHARE_STATISTICS = "smartdining:socialmedias:linkedin/getOrganizationShareStatistics",
    LINKEDIN_GET_POST_SHARE_STATISTICS = "smartdining:socialmedias:linkedin/getPostShareStatistics",
    LINKEDIN_GET_POST_COMMENTS = "smartdining:socialmedias:linkedin/getPostComments",
    LINKEDIN_CREATE_POST_COMMENT = "smartdining:socialmedias:linkedin/createPostComment",
    LINKEDIN_CREATE_POST = "smartdining:socialmedias:linkedin/createPost",
    LINKEDIN_UPDATE_POST = "smartdining:socialmedias:linkedin/updatePost",
    LINKEDIN_CREATE_IMAGE = "smartdining:socialmedias:linkedin/createImage",
    LINKEDIN_CREATE_VIDEO = "smartdining:socialmedias:linkedin/createVideo",
    LINKEDIN_CREATE_IV_POST = "smartdining:socialmedias:linkedin/createIVPost",
    LINKEDIN_CREATE_MULTI_IMAGE_POST = "smartdining:socialmedias:linkedin/createMultiImagePost",
    LINKEDIN_UPLOAD_MEDIA = "smartdining:socialmedias:linkedin/uploadMedia",
    LINKEDIN_GET_LINKEDIN_VIDEO = "smartdining:socialmedias:linkedin/getLinkedinVideo",
    LINKEDIN_FINALIZE_LINKEDIN_VIDEO = "smartdining:socialmedias:linkedin/finalizeLinkedinVideo",
    LINKEDIN_GET_IMAGE = "smartdining:socialmedias:linkedin/getImage",
    FACEBOOK_GET_USER_ACCESS_TOKEN_BY_CODE = "smartdining:socialmedias:facebook/getFacebookUserAccessTokenByCode",
    FACEBOOK_GET_REFRESH_ACCESS_TOKEN = "smartdining:socialmedias:facebook/getFacebookRefreshAccessToken",
    FACEBOOK_GET_USER_INFO = "smartdining:socialmedias:facebook/getFacebookUserInfo",
    FACEBOOK_GET_PAGES = "smartdining:socialmedias:facebook/getFacebookPages",
    FACEBOOK_GET_PAGE_DETAILS = "smartdining:socialmedias:facebook/getFacebookPageDetails",
    FACEBOOK_GET_PAGE_STATISTICS = "smartdining:socialmedias:facebook/getFacebookPageStatistics",
    FACEBOOK_GET_PAGE_RATING = "smartdining:socialmedias:facebook/getFacebookPageRating",
    FACEBOOK_GET_PAGE_POSTS = "smartdining:socialmedias:facebook/getFacebookPagePosts",
    FACEBOOK_GET_POST_DETAILS = "smartdining:socialmedias:facebook/getFacebookPostDetails",
    FACEBOOK_GET_POST_COMMENTS = "smartdining:socialmedias:facebook/getFacebookPostComments",
    FACEBOOK_ADD_POST_COMMENT = "smartdining:socialmedias:facebook/addFacebookPostComment",
    FACEBOOK_UPDATE_POST_COMMENT = "smartdining:socialmedias:facebook/updateFacebookPostComment",
    FACEBOOK_GET_POST_COMMENT_DETAILS = "smartdining:socialmedias:facebook/getFacebookPostCommentDetails",
    FACEBOOK_DELETE_POST_COMMENT = "smartdining:socialmedias:facebook/deleteFacebookPostComment",
    FACEBOOK_GET_POST_IMPRESSIONS = "smartdining:socialmedias:facebook/getFacebookPostImpressions",
    FACEBOOK_CREATE_POST = "smartdining:socialmedias:facebook/createFacebookPost",
    FACEBOOK_UPDATE_POST = "smartdining:socialmedias:facebook/updateFacebookPost",
    FACEBOOK_CREATE_PICTURE_POST = "smartdining:socialmedias:facebook/createFacebookPicturePost",
    FACEBOOK_CREATE_VIDEO_POST_FROM_CLOUD_STORAGE = "smartdining:socialmedias:facebook/createFacebookVideoPostFromCloudStorage",
    FACEBOOK_UPLOAD_MEDIA_DATA = "smartdining:socialmedias:facebook/upLoadFacebookMediaData",
    FACEBOOK_UPLOAD_MANY_MEDIA_DATA = "smartdining:socialmedias:facebook/upLoadManyFacebookMediaData",
    FACEBOOK_INIT_VIDEO_UPLOAD_SESSION = "smartdining:socialmedias:facebook/initVideoUploadSession",
    FACEBOOK_UPLOAD_VIDEO_POST = "smartdining:socialmedias:facebook/uploadVideoPost",
    FACEBOOK_FINISH_POST_VIDEO_IMPORTING = "smartdining:socialmedias:facebook/finishPostVideoImporting",
    INSTAGRAM_GET_INSTA_ACCOUNT = "smartdining:socialmedias:instagram/getInstaAccount",
    INSTAGRAM_GET_INSTA_ACCOUNT_DETAILS = "smartdining:socialmedias:instagram/getInstaAccountDetails",
    INSTAGRAM_GET_INSTA_ACCOUNT_INSIGHTS = "smartdining:socialmedias:instagram/getInstaAccountinsights",
    INSTAGRAM_GET_INSTA_MEDIA_POSTS = "smartdining:socialmedias:instagram/getInstaMediaPosts",
    INSTAGRAM_GET_MEDIA_POST_DETAILS = "smartdining:socialmedias:instagram/getMediaPostDetails",
    INSTAGRAM_GET_INSTA_STORIES = "smartdining:socialmedias:instagram/getInstaStories",
    INSTAGRAM_GET_MEDIA_STORY_DETAILS = "smartdining:socialmedias:instagram/getMediaStoryDetails",
    INSTAGRAM_GET_CAROUSEL_MEDIA_CHILD_DETAILS = "smartdining:socialmedias:instagram/getCarouselMediaChildDetails",
    INSTAGRAM_GET_INSTA_POST_COMMENTS = "smartdining:socialmedias:instagram/getInstaPostComments",
    INSTAGRAM_GET_MEDIA_COMMENT_DETAILS = "smartdining:socialmedias:instagram/getMediaCommentDetails",
    INSTAGRAM_ADD_MEDIA_COMMENT = "smartdining:socialmedias:instagram/addMediaComment",
    INSTAGRAM_HIDE_OR_DISPLAY_MEDIA_COMMENT = "smartdining:socialmedias:instagram/hideOrDisplayMediaComment",
    INSTAGRAM_REMOVE_MEDIA_COMMENT = "smartdining:socialmedias:instagram/removeMediaComment",
    INSTAGRAM_PUBLISH_MEDIA_POST_CONTAINER = "smartdining:socialmedias:instagram/publishMediaPostContainer",
    INSTAGRAM_CREATE_ON_MEDIA_POST_CONTAINER = "smartdining:socialmedias:instagram/createOnMediaPostContainer",
    INSTAGRAM_CREATE_MANY_MEDIA = "smartdining:socialmedias:instagram/createManyMedia",
    INSTAGRAM_CREATE_CAROUSEL_MEDIA_POST_CONTAINER = "smartdining:socialmedias:instagram/createCarouselMediaPostContainer",
    INSTAGRAM_GET_INSTA_POST_INSIGHTS = "smartdining:socialmedias:instagram/getInstaPostInsights",
    INSTAGRAM_GET_INSTA_POST_INSIGHTS_WITH_PROFILE_ACTIVITY = "smartdining:socialmedias:instagram/getInstaPostInsightsWithProfileActivity",
    INSTAGRAM_GET_INSTA_STORY_INSIGHTS = "smartdining:socialmedias:instagram/getInstaStoryInsights",
    LOAD_RESY_BOOKING = "smartdining:legacy:load_resy_booking",
    GET_GLOBAL_REPORT = "smartdining:legacy:get_global_report",
    DELETE_ICON_TEMPLATE = "smartdining:legacy:delete_icon_template",
    CREATE_ICON_TEMPLATE = "smartdining:legacy:create_icon_template",
    UPDATE_ICON_TEMPLATE = "smartdining:legacy:update_icon_template",
    ADD_MODIFIER_GROUP = "smartdining:legacy:add_modifier_group",
    REMOVE_MODIFIER_GROUP = "smartdining:legacy:remove_modifier_group",
    CREATE_MANAGER_ROLE = "smartdining:legacy:create_manager_role",
    NOTIFY_LATE_ORDER = "smartdining:legacy:notify_late_order",
    CREATE_DELIVER = "smartdining:legacy:create_deliver",
    // Mailing List Contact
    UPDATE_DELIVER = "smartdining:legacy:update_deliver",
    DELETE_DELIVER = "smartdining:legacy:delete_deliver",
    DISABLE_DELIVER = "smartdining:legacy:disable_deliver",
    ENABLE_DELIVER = "smartdining:legacy:enable_deliver",


    ACCEPT_PAYMENT = "smartdining:legacy:accept_payment",
    DECODE_PAYOUT_PAYMENT_METHOD = "smartdining:legacy:decode_payout_payment_method",
    GET_DELIVER_PAYOUTS = "smartdining:legacy:get_deliver_payouts",
    COMPLETE_PAYOUT = "smartdining:legacy:complete_payout",
    PROCESS_PAYOUT = "smartdining:legacy:process_payout",
    GET_RESTAURANT_PAYOUTS = "smartdining:legacy:get_restaurant_payouts",
    SEARCH_CHECK_PAYMENT = "smartdining:legacy:search_check_payment",
    REFUND_CHECK_PAYMENT = "smartdining:legacy:refund_check_payment",
    GET_CHECK_PAYMENT = "smartdining:legacy:get_check_payment",
    UPDATE_WALLET_STATUS = "smartdining:legacy:update_wallet_status",
    FLAG_WALLET = "smartdining:legacy:flag_wallet",
    MANAGE_FRAUD_CACHE = "smartdining:legacy:manage_fraud_cache",
    WHITELIST_WALLET = "smartdining:legacy:whitelist_wallet",

    // Applications Management
    MANAGE_APPLICATIONS = "smartdining:legacy:manage_applications",
    ASSIGN_DELIVER_TO_ORDER = "smartdining:legacy:assign_deliver_to_order",
    UNASSIGN_DELIVER_FROM_ORDER = "smartdining:legacy:unassign_deliver_from_order",
    MANAGE_WALLET = "smartdining:legacy:manage_wallet",
    CREATE_ZONE = "smartdining:legacy:create_zone",
    VIEW_ZONES = "smartdining:legacy:view_zones",
    UPDATE_ZONE = "smartdining:legacy:update_zone",
    DELETE_ZONE = "smartdining:legacy:delete_zone",
    DELIVERY_GET = "delivery:view",
    DELIVERY_GET_ALT = "delivery:views",
    DELIVERY_CREATE = "delivery:create",
    DELIVERY_UPDATE = "delivery:update",
    DELIVERY_DELETE = "delivery:delete",
    CREATE_DELIVERY_SETTINGS = "smartdining:legacy:create_delivery_settings",
    VIEW_DELIVERY_SETTINGS = "smartdining:legacy:view_delivery_settings",
    UPDATE_DELIVERY_SETTINGS = "smartdining:legacy:update_delivery_settings",
    DELETE_DELIVERY_SETTINGS = "smartdining:legacy:delete_delivery_settings",
}

/**
 * IAM allow-action strings from `GET /api/v1/roles/getUserPermissions` for platform staff (`ACCOUNT_MANAGER` roles).
 * Use together with {@link RESOURCE_PERMISSIONS} in sidebar `permissions` so sales / support reps see menus when the API
 * grants these actions (super users still receive the full {@link RESOURCE_PERMISSIONS} set in the client).
 */
export const IAM_MENU_ACTIONS = {
    organizationsGet: "organizations:get",
    organizationsCreateAccount: "legacy:add_organization_account",
    organizationUpdate: "organization:update",
    organizationEnable: "organization:enable",
    organizationDisable: "organization:disable",
    organizationDelete: "organization:delete",
    organizationsAssign: "organizations:assign",
    organizationsProvisionDemo: "organizations:provisionDemoTenant",
    organizationsProvisionDemoLegacy: "organizations:provisionDemo",
    organizationsProvisionTest: "organizations:provisionTestTenant",
    organizationsSeedTestTenant: "organizations:seedTestTenant",
    organizationsResetTestSandbox: "organizations:resetTestSandbox",
    organizationsResetTestOwnerPassword: "organizations:resetTestOwnerPassword",
    organizationsConvertOrgToTest: "organizations:convertOrgToTestTenant",
    organizationsConvertTestToProduction: "organizations:convertTestOrgToProduction",
    organizationsResetDemo: "organizations:resetDemo",
    organizationsLaunchDemo: "organizations:launchDemo",
    organizationsLaunchDemoTenant: "organizations:launchDemoTenant",
    organizationsRevokeDemoLaunchSession: "organizations:revokeDemoLaunchSession",
    organizationsExtendDemo: "organizations:extendDemo",
    organizationsExtendDemoTenant: "organizations:extendDemoTenant",
    organizationsDisableDemoTenant: "organizations:disableDemoTenant",
    organizationsArchiveDemoTenant: "organizations:archiveDemoTenant",
    organizationsResetDemoSandbox: "organizations:resetDemoSandbox",
  demoAccessCreateInvitation: "demoAccess:createInvitation",
  demoAccessRevokeInvitation: "demoAccess:revokeInvitation",
  demoAccessListInvitations: "demoAccess:listInvitations",
    organizationsDemoConversionReadinessCheck: "organizations:demoConversionReadinessCheck",
    organizationsDemoConversionCreateProduction: "organizations:demoConversionCreateProduction",
    organizationsSeedDemoTenant: "organizations:seedDemoTenant",
    organizationsResetDemoOwnerPassword: "organizations:resetDemoOwnerPassword",
    organizationsSoftDeleteDemoTenant: "organizations:softDeleteDemoTenant",
    organizationsStartDemoTour: "organizations:startDemoTour",
    demoAnalyticsRead: "demoAnalytics:read",
    demoOperationsRead: "demoOperations:read",
    demoSalesEnablementRead: "demoSalesEnablement:read",
    demoSalesEnablementGenerate: "demoSalesEnablement:generate",
    demoSalesEnablementUpdate: "demoSalesEnablement:update",
    demoSalesEnablementGenerateDraft: "demoSalesEnablement:generateDraft",
    demoSalesEnablementCreateHandoff: "demoSalesEnablement:createHandoff",
    demoSalesPipelineRead: "demoSalesPipeline:read",
    demoSalesPipelineCreate: "demoSalesPipeline:create",
    demoSalesPipelineUpdate: "demoSalesPipeline:update",
    demoSalesPipelineCreateTask: "demoSalesPipeline:createTask",
    demoSalesPipelineUpdateTask: "demoSalesPipeline:updateTask",
    demoCrmRead: "demoCrm:read",
    demoCrmLinkCompany: "demoCrm:linkCompany",
    demoCrmSyncContacts: "demoCrm:syncContacts",
    demoCrmSyncRecap: "demoCrm:syncRecap",
    demoCrmSyncOpportunity: "demoCrm:syncOpportunity",
    internalCrmDemoAutomationRead: "internalCrmDemoAutomation:read",
    internalCrmDemoAutomationRun: "internalCrmDemoAutomation:run",
    internalCrmDemoAutomationPause: "internalCrmDemoAutomation:pause",
    internalCrmDemoAutomationResume: "internalCrmDemoAutomation:resume",
    internalCrmSalesCommandCenterRead: "internalCrmSalesCommandCenter:read",
    internalCrmPlaybookRead: "internalCrmPlaybook:read",
    internalCrmPlaybookApply: "internalCrmPlaybook:apply",
    internalCrmPlaybookUpdateProgress: "internalCrmPlaybook:updateProgress",
    internalCrmPlaybookAdvanceStage: "internalCrmPlaybook:advanceStage",
    internalCrmPlaybookRequestManagerReview: "internalCrmPlaybook:requestManagerReview",
    internalCrmPlaybookManagerReview: "internalCrmPlaybook:managerReview",
    internalCrmFollowUpRead: "internalCrmFollowUp:read",
    internalCrmFollowUpGenerate: "internalCrmFollowUp:generate",
    internalCrmFollowUpUpdate: "internalCrmFollowUp:update",
    internalCrmFollowUpApprove: "internalCrmFollowUp:approve",
    internalCrmFollowUpLogCommunication: "internalCrmFollowUp:logCommunication",
    internalCrmDataQualityRead: "internalCrmDataQuality:read",
    internalCrmDataQualityMergePreview: "internalCrmDataQuality:mergePreview",
    internalCrmDataQualityMerge: "internalCrmDataQuality:merge",
    internalCrmDataQualityDismiss: "internalCrmDataQuality:dismiss",
    internalCrmDemoAutomationDismiss: "internalCrmDemoAutomation:dismiss",
    internalCrmRead: "internalCrm:read",
    internalCrmReadAll: "internalCrm:readAll",
    internalCrmReadAssigned: "internalCrm:readAssigned",
    internalCrmAssignOwner: "internalCrm:assignOwner",
    internalCrmChangeStage: "internalCrm:changeStage",
    internalCrmMarkWon: "internalCrm:markWon",
    internalCrmMarkLost: "internalCrm:markLost",
    internalCrmMergeRecords: "internalCrm:mergeRecords",
    internalCrmOverridePlaybookCriteria: "internalCrm:overridePlaybookCriteria",
    internalCrmApproveManagerReview: "internalCrm:approveManagerReview",
    internalCrmApproveFollowUpDraft: "internalCrm:approveFollowUpDraft",
    internalCrmRunAutomation: "internalCrm:runAutomation",
    internalCrmPauseAutomation: "internalCrm:pauseAutomation",
    internalCrmViewAudit: "internalCrm:viewAudit",
    demoCrmViewEngagement: "demoCrm:viewEngagement",
    demoCrmStartConversionReadiness: "demoCrm:startConversionReadiness",
    demoAnalyticsStartConversion: "demoAnalytics:startConversion",
    demoPacksGet: "demoPacks:get",
    demoPacksProvisionDemoTenant: "demoPacks:provisionDemoTenant",
    demoPacksApply: "demoPacks:apply",
    restaurantsGet: "restaurants:get",
    restaurantsSearch: "restaurants:search",
    cuisinesSearch: "cuisines:search",
    apiKeysCreate: "apikeys:create",
    apiKeysGet: "apikeys:get",
    apiKeysUpdate: "apikeys:update",
    apiKeysRevoke: "apikeys:revoke",
    apiKeysRotate: "apikeys:rotate",
} as const;

/** Organization API key management — matches backend `IamAction.ApiKey` + legacy catalog key. */
export const ORGANIZATION_API_KEYS_PERMISSIONS: string[] = [
    RESOURCE_PERMISSIONS.ADMIN_ORGANIZATION_API_KEYS,
    IAM_MENU_ACTIONS.apiKeysGet,
    IAM_MENU_ACTIONS.apiKeysCreate,
    IAM_MENU_ACTIONS.apiKeysUpdate,
    IAM_MENU_ACTIONS.apiKeysRevoke,
    IAM_MENU_ACTIONS.apiKeysRotate,
];

/**
 * CRM / sales nav: IAM codes plus legacy path-shaped strings still carried on older JWTs and roles until fully migrated.
 */
export const SALES_LEADS_LIST_MENU_PERMISSIONS: string[] = [
    RESOURCE_PERMISSIONS.GET_SALES_LEADS,
    RESOURCE_PERMISSIONS.SALES_LEADS_GET_SALES_LEADS,
    '/sales-leads',
    '/salesLeads/getSalesLeads',
];